import Button, { SizeType } from '@components/atoms/button'
import CurriculumDownloadModal from '@components/home/curriculum-download-modal'
import CourseVideoModal from '@components/home/course-video-modal'
import CourseCard from '@components/locale-pages/home/course-card'
import { getTrialSubject } from '@lib/constants/booking'
import { COURSE_FIRST_FOLD } from '@lib/data/course-landing-page'
import { Trans, defineMessage } from '@lingui/macro'
import Link from 'next/link'
import { useState } from 'react'
import {
  PSC_COURSES_GRADE_1_2,
  PSC_COURSES_GRADE_3_5,
} from '@lib/data/public-speaking-hub'

// Course codes (keys of COURSE_INFO) for the first 3 AI cards that stay
// hardcoded on the homepage fold; the rest come from the official courses API.
const HOMEPAGE_PINNED_COURSE_CODES = ['cgm', 'cp', 'cc']

// Bunny Stream library that hosts the course intro videos (same library the
// hardcoded cards embed).
const BUNNY_STREAM_LIBRARY_ID = '239253'
const BUNNY_VIDEO_GUID_PATTERN = /b-cdn\.net\/([0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12})/i

// Render dynamic course intro videos exactly like the hardcoded cards: convert
// a raw Bunny CDN URL (e.g. .../{guid}/playlist.m3u8) into the iframe embed URL
// so it uses Bunny's player instead of the browser's native media viewer.
const toBunnyEmbedUrl = (url: string): string => {
  if (url.includes('iframe.mediadelivery.net/embed/')) return url

  const match = url.match(BUNNY_VIDEO_GUID_PATTERN)
  if (!match) return url

  return (
    `https://iframe.mediadelivery.net/embed/${BUNNY_STREAM_LIBRARY_ID}/${match[1]}` +
    '?autoplay=true&loop=false&muted=false&preload=true&responsive=true'
  )
}

const HOVER_BG = [
  'hover:bg-orange-100 hover:border-orange',
  'hover:bg-orange2-50 hover:border-orange2-400 hover:bg-opacity-75',
  'hover:bg-grey-200 hover:border-grey',
  'hover:bg-blue-50 hover:border-blue-300 hover:bg-opacity-50',
  'hover:bg-red-50 hover:border-red-200',
  'hover:bg-yellow-100 hover:bg-opacity-50 hover:border-yellow-200',
  'hover:bg-violet-50 hover:border-violet-100',
  'hover:bg-pink hover:border-pink',
]

const COURSE_PAGE_URL = {
  codingGrandMaster: '/courses/coding-grandmaster/',
  codingProdigy: '/courses/coding-prodigy/',
  codingchampion: '/courses/coding-champion/',
  scratchPrograming: '/courses/scratch-programming/',
  pythonForKids: '/courses/python-for-kids/',
  gameDevelopment: '/courses/game-development/',
  webDevelopment: 'https://www.codingal.com/courses/web-development/',
  appDevelopment: 'https://www.codingal.com/courses/app-development/',
  dataScience: 'https://www.codingal.com/courses/data-science/',
  roblox: 'https://www.codingal.com/courses/roblox-coding/',
  APComputerScienceA: 'https://www.codingal.com/courses/ap-computer-science-a/',
  IoiAlgorithms: 'https://www.codingal.com/courses/ioi-algorithms-course/',
  aiAndMl: 'https://www.codingal.com/courses/ai-and-machine-learning/',
}

export type CourseInfoType = {
  title: JSX.Element
  titleCompactView: JSX.Element
  grade: string
  activites: number
  lessons: number
  duration: string
  Age: string
  description: any
  image: string
  altText: string
  outcomes: any[]
  courseCode: string
  curriculumPDFLink: string
  trialCourseQuery: string
  isBestSeller: boolean
  coursePageUrl?: string
  gradeOptions?: number[]
  isAICourse?: boolean
  prerequisite?: JSX.Element
  curriculumVideos?: CurriculumVideoType[]
}

export type CurriculumVideoType = {
  grades?: string
  url: string
  altText?: string
  image?: string
}

export type CourseVideoType = {
  title: string
  url?: string
  image: string
  altText: string
}

export type CourseFeaturesType = {
  outcomes: string[]
  skills: {
    imagePath: string
    skillName: string
  }[]
}

export type CourseLearningType = {
  title: string
  description: JSX.Element
  image: string
}

export const COURSE_LEARNING: Record<string, CourseLearningType[]> = {
  THUNKABLE: [
    {
      title: 'Real-world coding tools',
      image: 'coding-tools',
      description: (
        <>
          Students get hands-on experience with Thunkable’s online coding
          environment, where they can write and run Python, HTML, and JavaScript
          code directly in their browser—no downloads required! By learning
          through Thunkable, kids understand how real-world coding works while
          instantly seeing the results of their creativity. It’s the perfect
          first step toward mastering programming languages in a fun and
          accessible way.
        </>
      ),
    },
    {
      title: 'Creative projects',
      image: 'design-thinking',
      description: (
        <>
          Our Thunkable-based projects are designed to make learning coding
          exciting and imaginative. Kids can draw colorful patterns with
          Python’s Turtle module, build fun interactive stories, or even create
          math and science visualizations. Every project helps them apply their
          coding knowledge, explore new ideas, and express creativity through
          real programming.
        </>
      ),
    },
    {
      title: 'Interactive learning',
      image: 'problem-solving-2',
      description: (
        <>
          At Codingal, learning on Thunkable is never boring! Through hands-on
          activities, challenges, and guided projects, students see their code
          come alive instantly on the screen. Each lesson builds on the last,
          ensuring kids grasp concepts step by step while gaining confidence as
          young coders.
        </>
      ),
    },
    {
      title: 'Focus on problem solving and critical thinking',
      image: 'logical-thinking',
      description: (
        <>
          Thunkable at Codingal teaches kids more than just how to type code, it
          teaches them how to think like programmers. Every activity encourages
          students to solve problems creatively, test their ideas, and improve
          their projects. This helps build critical thinking, logical reasoning,
          and debugging skills in a fun and visual way.
        </>
      ),
    },
    {
      title: 'Easy accessibility',
      image: 'easy-accessibility',
      description: (
        <>
          Learning Thunkable with Codingal is simple and flexible. Since
          Thunkable runs entirely online, kids can access their coding projects
          from any device, anytime. With class recordings, step-by-step lessons,
          and round-the-clock access to resources, students can learn at their
          own pace and continue building amazing Thunkable projects wherever
          they are.
        </>
      ),
    },
  ],
  JAVA: [
    {
      title: 'Real-World Coding Tools',
      image: 'coding-tools',
      description: (
        <>
          Your child will gain hands-on experience with industry-standard tools
          like Java, Visual Studio Code, Git, and GitHub. These tools not only
          teach the basics of programming but also prepare students for
          real-world challenges in technology. By mastering them, your child
          will develop critical skills that align with modern tech environments,
          giving them a head start in their technology journey.
        </>
      ),
    },
    {
      title: 'Creative Projects',
      image: 'design-thinking',
      description: (
        <>
          Our curriculum features a range of creative projects designed to make
          learning Java engaging and meaningful. Students solve coding problems
          and master algorithms to tackle complex tasks. Each project encourages
          creativity while reinforcing key programming concepts, ensuring that
          learning is both enjoyable and effective.
        </>
      ),
    },
    {
      title: 'Interactive Learning',
      image: 'problem-solving-2',
      description: (
        <>
          We provide a dynamic learning experience through engaging activities,
          assignments, and quizzes. Our interactive approach keeps learners
          motivated by allowing them to apply knowledge right away. Tasks
          gradually increase in difficulty, ensuring continuous learning while
          reinforcing previously taught concepts.
        </>
      ),
    },
    {
      title: 'Focus on Problem-Solving and Critical Thinking',
      image: 'logical-thinking',
      description: (
        <>
          Through coding, students develop strong problem-solving and
          critical-thinking skills, essential for success in programming and
          beyond. By working on challenging coding problems and collaborative
          projects, your child will learn how to analyze situations, develop
          logical solutions, and think creatively.
        </>
      ),
    },
    {
      title: 'Easy Accessibility',
      image: 'easy-accessibility',
      description: (
        <>
          Learning with Codingal is flexible and convenient. Class recordings
          and detailed lesson plans are available so kids can access materials
          anytime. This flexibility ensures that learning fits seamlessly into
          their schedule, allowing them to progress at their own pace with
          expert guidance and a supportive community.
        </>
      ),
    },
  ],
  TRINKET: [
    {
      title: 'Real-World Coding Tools',
      image: 'coding-tools',
      description: (
        <>
          Students get hands-on experience with Trinket’s online coding
          environment, where they can write and run Python, HTML, and JavaScript
          code directly in their browser—no downloads required! By learning
          through Trinket, kids understand how real-world coding works while
          instantly seeing the results of their creativity. It’s the perfect
          first step toward mastering programming languages in a fun and
          accessible way.
        </>
      ),
    },
    {
      title: 'Creative Projects',
      image: 'design-thinking',
      description: (
        <>
          Our Trinket-based projects are designed to make learning coding
          exciting and imaginative. Kids can draw colorful patterns with
          Python’s Turtle module, build fun interactive stories, or even create
          math and science visualizations. Every project helps them apply their
          coding knowledge, explore new ideas, and express creativity through
          real programming.
        </>
      ),
    },
    {
      title: 'Interactive Learning',
      image: 'problem-solving-2',
      description: (
        <>
          At Codingal, learning on Trinket is never boring! Through hands-on
          activities, challenges, and guided projects, students see their code
          come alive instantly on the screen. Each lesson builds on the last,
          ensuring kids grasp concepts step by step while gaining confidence as
          young coders.
        </>
      ),
    },
    {
      title: 'Focus on Problem-Solving and Critical Thinking',
      image: 'logical-thinking',
      description: (
        <>
          Trinket at Codingal teaches kids more than just how to type code—it
          teaches them how to think like programmers. Every activity encourages
          students to solve problems creatively, test their ideas, and improve
          their projects. This helps build critical thinking, logical reasoning,
          and debugging skills in a fun and visual way.
        </>
      ),
    },
    {
      title: 'Easy Accessibility',
      image: 'easy-accessibility',
      description: (
        <>
          Learning Trinket with Codingal is simple and flexible. Since Trinket
          runs entirely online, kids can access their coding projects from any
          device, anytime. With class recordings, step-by-step lessons, and
          round-the-clock access to resources, students can learn at their own
          pace and continue building amazing Trinket projects wherever they are.
        </>
      ),
    },
  ],
  PYGAME: [
    {
      title: 'Hands-on experience with industry tools',
      image: 'animation-and-motion-control',
      description: (
        <>
          Students learn to design and build games using Pygame, a powerful
          Python library for game development. They use simple tools like Python
          IDEs and professional platforms such as Visual Studio Code, setting
          them up for future success in coding, AI, and interactive digital
          design — how cool is that?!
        </>
      ),
    },
    {
      title: 'Exciting Pygame projects',
      image: 'laptop-coding',
      description: (
        <>
          From bouncing ball animations and racing games to adventure-style
          challenges, students create real-world Pygame projects. These projects
          help kids understand how games are structured, how characters
          interact, and how logic brings stories to life — imagine the games
          they’ll create!
        </>
      ),
    },
    {
      title: 'Interactive and progressive learning',
      image: 'analytical-thinking',
      description: (
        <>
          Kids follow a curriculum designed to grow with them — starting from
          basic shapes and movements to building full interactive games with
          scoring systems, levels, and sound effects. Along the way, they
          receive instant feedback, solve live coding challenges, and enjoy
          quizzes — learning has never been this fun!
        </>
      ),
    },
    {
      title: 'Flexibility and accessibility',
      image: 'setting-arrow',
      description: (
        <>
          Whether attending live classes or reviewing recorded lessons, students
          can learn Pygame anytime, anywhere. With round-the-clock access to
          resources and instructor support, learning never has to stop — a
          convenient option for busy families!
        </>
      ),
    },
    {
      title: 'Focus on creativity and problem-solving',
      image: 'logical-thinking',
      description: (
        <>
          Students use Pygame to creatively design characters, code
          interactions, and bring their imagination to life through games. They
          learn to troubleshoot errors, apply logical problem-solving, and build
          fun, playable projects — essential skills for the innovators of
          tomorrow. 🚀
        </>
      ),
    },
  ],
  ANIMATION: [
    {
      title: 'Real-World Coding Tools',
      image: 'coding-tools',
      description: (
        <>
          Students gain hands-on experience with tools and platforms like
          Scratch, Code.org, Python with Pygame, Web Development (HTML, CSS,
          JavaScript), and Roblox Studio. These platforms not only make learning
          animation accessible but also prepare kids for real-world applications
          in technology. By working with these tools, students develop coding
          expertise while bringing their creative ideas to life.
        </>
      ),
    },
    {
      title: 'Creative Projects',
      image: 'design-thinking',
      description: (
        <>
          Our curriculum is packed with imaginative projects designed to make
          learning animation engaging and enjoyable. Kids will create
          interactive stories, animated games, dynamic websites, and even 3D
          experiences in Roblox. Every project encourages kids to think
          creatively, apply their coding knowledge, and master animation
          concepts while having a blast!
        </>
      ),
    },
    {
      title: 'Interactive Learning',
      image: 'problem-solving-2',
      description: (
        <>
          At Codingal, we make learning dynamic and interactive. Through
          hands-on activities, coding challenges, and quizzes, students actively
          apply what they learn and watch their animations come to life. Our
          curriculum is designed to gradually introduce new concepts, helping
          students build confidence and coding skills step by step.
        </>
      ),
    },
    {
      title: 'Focus on Problem-Solving and Critical Thinking',
      image: 'logical-thinking',
      description: (
        <>
          Animation at Codingal is all about creativity and problem-solving.
          Students not only learn to code but also design their animations with
          logic and fun solutions. They’ll develop critical thinking skills as
          they analyze challenges, create unique animations, and bring their
          ideas to life in a way that’s both interactive and visually exciting.
        </>
      ),
    },
    {
      title: 'Easy Accessibility',
      image: 'easy-accessibility',
      description: (
        <>
          Learning animation with Codingal is flexible and convenient. With
          class recordings, detailed lesson plans, and 24/7 access to resources,
          students can learn at their own pace and revisit concepts whenever
          they need. This flexibility ensures kids stay motivated and continue
          progressing on their creative animation journey.
        </>
      ),
    },
  ],
  TURTLE: [
    {
      title: 'Real-World Coding Experience',
      image: 'coding-tools',
      description: (
        <>
          Students get hands-on experience with Python’s built-in Turtle module,
          where they write real Python code and instantly see the results on
          screen. There’s no need for complex setups — just simple commands that
          bring creativity to life. By coding with Python Turtle, kids learn how
          real programming works through visuals, movement, and color. It’s the
          perfect first step toward mastering text-based coding.
        </>
      ),
    },
    {
      title: 'Creative Projects',
      image: 'design-thinking',
      description: (
        <>
          Our Python Turtle projects are designed to make learning coding
          exciting and imaginative. Kids draw colorful geometric patterns,
          animate digital art, or even create fun math and science
          visualizations. Every project helps them apply logic, explore design,
          and develop computational thinking through creative expression.
        </>
      ),
    },
    {
      title: 'Interactive Learning',
      image: 'problem-solving-2',
      description: (
        <>
          Learning with Python Turtle at Codingal is never passive. Each session
          includes hands-on coding, fun challenges, and guided experiments that
          let kids see their code come alive instantly. Lessons are structured
          to build progressively, from simple movements to complex
          designs—helping students gain confidence as young coders.
        </>
      ),
    },
    {
      title: 'Focus on Problem-Solving and Critical Thinking',
      image: 'logical-thinking',
      description: (
        <>
          Python Turtle teaches kids much more than drawing with code—it teaches
          them to think like programmers. Each activity encourages them to plan
          steps, fix errors, and refine results. This approach strengthens
          critical thinking, logical reasoning, and debugging skills in an
          enjoyable, visual way.
        </>
      ),
    },
    {
      title: 'Easy Accessibility',
      image: 'easy-accessibility',
      description: (
        <>
          Learning Python Turtle with Codingal is simple and flexible. Kids can
          write and run Python Turtle code directly in a browser-based
          environment like Replit or Trinket, without installing any software.
          With class recordings, guided projects, and 24/7 access to learning
          resources, they can practice anytime and continue building creative
          Python Turtle projects wherever they are.
        </>
      ),
    },
  ],

  MIT_APP_INVENTOR: [
    {
      title: 'Hands-On Experience with Industry Tools',
      image: 'animation-and-motion-control',
      description: (
        <>
          Students get hands-on experience using MIT App Inventor—an intuitive,
          block-based development platform designed by MIT. With this tool, kids
          learn to build real Android mobile apps while developing a strong
          understanding of coding logic and mobile design.
        </>
      ),
    },
    {
      title: 'Exciting App Development Projects',
      image: 'laptop-coding',
      description: (
        <>
          The curriculum is packed with exciting projects like quiz apps,
          calculators, games, and even health trackers. These fun, creative
          challenges keep kids engaged as they learn how to design, code, and
          publish fully functional mobile apps using AI & coding principles.
        </>
      ),
    },
    {
      title: 'Interactive and Progressive Learning',
      image: 'analytical-thinking',
      description: (
        <>
          Learning with MIT App Inventor is highly visual and interactive.
          Students build apps through guided steps, gradually increasing the
          complexity of their creations. Concepts are introduced progressively
          to ensure students grow in confidence and skill with each lesson.
        </>
      ),
    },
    {
      title: 'Flexibility and Accessibility',
      image: 'setting-arrow',
      description: (
        <>
          Codingal&lsquo;s MIT App Inventor course offers 24/7 access to
          recorded lessons, live class support, and step-by-step resources.
          Whether your child is a beginner or advancing fast, the course adapts
          to their learning pace—anytime, anywhere.
        </>
      ),
    },
    {
      title: 'Focus on Creativity and Problem-Solving',
      image: 'logical-thinking',
      description: (
        <>
          Through real-world app creation, kids learn to break down user needs,
          design functional interfaces, and debug code. These activities boost
          creative thinking, critical analysis, and problem-solving—essential
          skills for both coding and future careers.
        </>
      ),
    },
  ],

  BOOTSTRAP: [
    {
      title: 'Hands-On Experience with Industry Tools',
      image: 'animation-and-motion-control',
      description: (
        <>
          Students learn to design and build games using Bootstrap, a powerful
          Python library for game development. They use simple tools like Python
          IDEs and professional platforms such as Visual Studio Code, setting
          them up for future success in coding, AI, and interactive digital
          design — how cool is that?!
        </>
      ),
    },
    {
      title: 'Exciting Bootstrap Projects',
      image: 'laptop-coding',
      description: (
        <>
          From bouncing ball animations and racing games to adventure-style
          challenges, students create real-world Bootstrap projects. These
          projects help kids understand how games are structured, how characters
          interact, and how logic brings stories to life, imagine the games
          they’ll create!
        </>
      ),
    },
    {
      title: 'Interactive and Progressive Learning',
      image: 'analytical-thinking',
      description: (
        <>
          Kids follow a curriculum designed to grow with them starting from
          basic shapes and movements to building full interactive games with
          scoring systems, levels, and sound effects. Along the way, they
          receive instant feedback, solve live coding challenges, and enjoy
          quizzes, learning has never been this fun!
        </>
      ),
    },
    {
      title: 'Flexibility and Accessibility',
      image: 'setting-arrow',
      description: (
        <>
          Whether attending live classes or reviewing recorded lessons, students
          can learn Bootstrap anytime, anywhere. With round-the-clock access to
          resources and instructor support, learning never has to stop, a
          convenient option for busy families!
        </>
      ),
    },
    {
      title: 'Focus on creativity and problem-solving,',
      image: 'logical-thinking',
      description: (
        <>
          Students use Bootstrap to creatively design characters, code
          interactions, and bring their imagination to life through games. They
          learn to troubleshoot errors, apply logical problem-solving, and build
          fun, playable projects, essential skills for the innovators of
          tomorrow.
        </>
      ),
    },
  ],
  JAVASCRIPT: [
    {
      title: 'Real-World Coding Experience',
      image: 'coding-tools',
      description: (
        <>
          Students get hands-on experience with JavaScript for Web Development,
          where they write real JavaScript code and instantly see results in
          their browser. There’s no need for complicated setups by just type,
          run, and watch their creations come alive. By coding with JavaScript,
          kids understand how modern websites and apps work through visuals,
          motion, and interactivity. It’s the perfect first step toward
          mastering front-end development and real-world programming.
        </>
      ),
    },
    {
      title: 'Creative Projects',
      image: 'design-thinking',
      description: (
        <>
          Our JavaScript projects make learning coding exciting and imaginative.
          Kids build interactive games, digital art, quizzes, and animated web
          pages while learning essential coding logic. Every project helps them
          apply problem-solving, explore design, and develop computational
          thinking through creativity and fun.
        </>
      ),
    },
    {
      title: 'Interactive learning',
      image: 'problem-solving-2',
      description: (
        <>
          Learning JavaScript at Codingal is dynamic and hands-on. Each class
          includes real coding, guided experiments, and fun challenges that let
          kids see their code in action instantly. Lessons progress from simple
          syntax and commands to advanced animations and user interactions,
          helping students grow step by step as confident young coders.
        </>
      ),
    },
    {
      title: 'Focus on problem solving and critical thinking',
      image: 'logical-thinking',
      description: (
        <>
          JavaScript teaches kids far more than just coding — it teaches them to
          think like developers. Each activity encourages them to plan, test,
          and refine their logic. This helps strengthen critical thinking,
          debugging, and analytical skills while keeping learning visual and
          engaging.
        </>
      ),
    },
    {
      title: 'Easy accessibility',
      image: 'easy-accessibility',
      description: (
        <>
          Learning JavaScript with Codingal is simple and flexible. Kids can
          write and run JavaScript code directly in browser-based platforms like
          Replit, p5.js, or CodePen, without needing to install any software.
          With class recordings, guided projects, and 24/7 resource access,
          students can practice anytime and continue building creative
          JavaScript projects wherever they are
        </>
      ),
    },
  ],
  CSS: [
    {
      title: 'Real world coding tools',
      image: 'coding-tools',
      description: (
        <>
          Students use industry-standard web technologies; HTML and CSS; inside
          a live browser-based code editor. They learn how to apply styles to
          real web elements, inspect elements on existing sites, and see changes
          take effect instantly. It’s hands-on, visual learning that mirrors how
          professional designers work. This is an excellent way to learn HTML
          and CSS for kids.
        </>
      ),
    },
    {
      title: 'Creative projects',
      image: 'design-thinking',
      description: (
        <>
          Every lesson culminates in a themed project; styled portfolios,
          personal landing pages, digital posters, and custom layouts. These
          creative tasks give students a chance to express themselves while
          mastering essential CSS properties and techniques like color schemes,
          spacing, positioning, and typography. This helps solidify their
          creative coding for kids journey.
        </>
      ),
    },
    {
      title: 'Interactive learning',
      image: 'problem-solving-2',
      description: (
        <>
          CSS offers immediate visual feedback, which keeps students engaged as
          they experiment with styles and layouts. Guided exercises help them
          transition from basic color changes to mastering complex concepts like
          the box model, pseudo-classes, and responsive design; all scaffolded
          for steady progress. This approach addresses the problem-solving web
          design process effectively.
        </>
      ),
    },
    {
      title: 'Focus on problem solving and critical thinking',
      image: 'logical-thinking',
      description: (
        <>
          As students learn to structure and style web pages, they also develop
          critical skills like layout planning, hierarchical styling, and
          debugging style conflicts. Each project challenges them to think
          creatively and solve design problems; skills essential to future
          careers in tech and UI/UX. This prepares them to become a future web
          designer.
        </>
      ),
    },
    {
      title: 'Easy accessibility',
      image: 'easy-accessibility',
      description: (
        <>
          Our CSS lessons for kids run completely online; no installations
          required. Students can log in anytime to access live sessions, class
          recordings, and downloadable resources. Whether on a tablet or laptop,
          they can style, preview, and refine their web pages from anywhere, at
          their own pace.
        </>
      ),
    },
  ],
  HTML: [
    {
      title: 'Hands-on experience with industry tools',
      image: 'animation-and-motion-control',
      description: (
        <>
          Students learn to build web pages using HTML, the foundation of the
          internet. They use simple tools like online editors and professional
          platforms like Visual Studio Code, setting them up for future success
          in web development and AI & coding.
        </>
      ),
    },
    {
      title: 'Exciting HTML projects',
      image: 'laptop-coding',
      description: (
        <>
          From personal bios and digital storybooks to functional web pages,
          students create real-world projects using just HTML. These projects
          teach kids how websites are structured and how they can bring their
          ideas to life on the web.
        </>
      ),
    },
    {
      title: 'Interactive and progressive learning',
      image: 'analytical-thinking',
      description: (
        <>
          Kids follow a curriculum designed to grow with them - starting from
          basic tags to more complex page structures. Along the way, they build
          their own sites, receive instant feedback, and enjoy learning through
          quizzes and live challenges.
        </>
      ),
    },
    {
      title: 'Flexibility and accessibility',
      image: 'setting-arrow',
      description: (
        <>
          Whether attending live classes or reviewing recorded lessons, students
          can learn HTML whenever and wherever they prefer. With round-the-clock
          access to resources and instructor support, learning never has to
          stop.
        </>
      ),
    },
    {
      title: 'Focus on creativity and problem-solving',
      image: 'logical-thinking',
      description: (
        <>
          Students use HTML to creatively organize and present information. They
          learn to troubleshoot formatting issues, structure content logically,
          and build sites that are both functional and engaging.
        </>
      ),
    },
  ],
  AICODE101: [
    {
      title: 'Real-World AI Tools',
      image: 'coding-tools',
      description: (
        <>
          Students get the chance to work with AI-powered tools while learning
          programming through block-based platforms. Whether they are creating
          AI-assisted projects like chatbots or building apps that interact with
          AI models, they gain exposure to real-world technologies and concepts
          in an easy-to-understand, engaging way.
        </>
      ),
    },
    {
      title: 'Creative projects',
      image: 'design-thinking',
      description: (
        <>
          Every lesson leads to an exciting project, such as designing
          interactive games, building AI-powered apps, or creating personalized
          assistants. These projects allow kids to apply what they have learnt
          in a fun, creative way while gaining practical experience in building
          real applications.
        </>
      ),
    },
    {
      title: 'Interactive learning',
      image: 'problem-solving-2',
      description: (
        <>
          AI Code 101 offers interactive, block-based lessons that guide
          students step by step, ensuring they stay engaged and learn at their
          own pace. The visual feedback helps them understand key concepts like
          problem-solving, logic building, and the relationship between code and
          AI.
        </>
      ),
    },
    {
      title: 'Focus on problem solving and critical thinking',
      image: 'logical-thinking',
      description: (
        <>
          Through challenges and AI-powered coding tasks, students develop
          essential problem-solving skills. They learn to break down complex
          tasks, design logical solutions, and work through coding problems
          creatively, preparing them for more advanced AI and programming
          concepts.
        </>
      ),
    },
    {
      title: 'Easy accessibility',
      image: 'easy-accessibility',
      description: (
        <>
          With online classes, students can access lessons, projects, and AI
          tools anytime, anywhere. Whether reviewing materials or tackling new
          challenges, they have the freedom to learn at their own pace with
          ongoing support from expert instructors.
        </>
      ),
    },
  ],
  CODE_ORG: [
    {
      title: 'Real-World AI Tools',
      image: 'coding-tools',
      description: (
        <>
          Students get the chance to work with AI-powered tools while learning
          programming through block-based platforms like Code.org. Whether they
          are creating AI-assisted projects like chatbots or building apps that
          interact with AI models, they gain exposure to real-world technologies
          and concepts in an easy-to-understand, engaging way.
        </>
      ),
    },
    {
      title: 'Creative projects',
      image: 'design-thinking',
      description: (
        <>
          Every lesson leads to an exciting project, such as designing
          interactive games, building AI-powered apps, or creating personalized
          assistants using Code.org. These projects allow kids to apply what
          they have learned in a fun, creative way while gaining practical
          experience in building real applications.
        </>
      ),
    },
    {
      title: 'Interactive learning',
      image: 'problem-solving-2',
      description: (
        <>
          AI Code 101 offers interactive, block-based lessons that guide
          students step by step, ensuring they stay engaged and learn at their
          own pace. The visual feedback helps them understand key concepts like
          problem-solving, logic building, and the relationship between code and
          AI, all while using Code.org as their learning platform.
        </>
      ),
    },
    {
      title: 'Focus on problem solving and critical thinking',
      image: 'logical-thinking',
      description: (
        <>
          Through challenges and AI-powered coding tasks on Code.org, students
          develop essential problem-solving skills. They learn to break down
          complex tasks, design logical solutions, and work through coding
          problems creatively, preparing them for more advanced AI and
          programming concepts.
        </>
      ),
    },
    {
      title: 'Easy accessibility',
      image: 'easy-accessibility',
      description: (
        <>
          With online Code.org classes, students can access lessons, projects,
          and AI tools anytime, anywhere. Whether reviewing materials or
          tackling new challenges, they have the freedom to learn at their own
          pace with ongoing support from expert instructors.
        </>
      ),
    },
  ],
  PICTOBLOX: [
    {
      title: 'Real-World AI Tools',
      image: 'coding-tools',
      description: (
        <>
          Students use PictoBlox’s AI and machine learning features like face
          detection, pose tracking, and built-in ChatGPT to build real-world
          applications. They get a feel for how modern AI works, all through
          drag-and-drop simplicity.
        </>
      ),
    },
    {
      title: 'Creative projects',
      image: 'design-thinking',
      description: (
        <>
          Every lesson ends with a project kids can proudly show off! Whether
          it’s building a smart tool or training AI to recognize something new.
          These hands-on tasks keep learning exciting while encouraging
          self-expression and innovation.
        </>
      ),
    },
    {
      title: 'Interactive learning',
      image: 'problem-solving-2',
      description: (
        <>
          PictoBlox’s visual coding interface makes learning intuitive and
          highly interactive. Step-by-step guidance helps students understand
          what AI is doing and why—making even complex topics approachable and
          fun.
        </>
      ),
    },
    {
      title: 'Focus on problem solving and critical thinking',
      image: 'logical-thinking',
      description: (
        <>
          From teaching machines how to “see” to debugging pose detection
          errors, kids practice logical reasoning, structured thinking, and
          creative problem-solving skills essential for the tech-powered future.
        </>
      ),
    },
    {
      title: 'Easy accessibility',
      image: 'easy-accessibility',
      description: (
        <>
          Everything runs online in the browser so no setup needed. Students get
          24/7 access to class recordings, support, and learning materials, so
          they can practice and progress at their own pace.
        </>
      ),
    },
  ],
  TEACHABLE_MACHINE: [
    {
      title: 'Real-World AI Tools',
      image: 'coding-tools',
      description: (
        <>
          Students use Google’s Teachable Machine to train real AI models and
          integrate them into projects using MIT Raise Playground and Scratch.
          They experience how AI recognizes images, sounds, gestures, and faces,
          just like modern smart devices.
        </>
      ),
    },
    {
      title: 'Creative projects',
      image: 'design-thinking',
      description: (
        <>
          Every unit ends with a show-off build like AI-powered games,
          interactive characters, and smart simulations. These hands-on projects
          keep kids excited to learn while helping them apply AI concepts in
          meaningful ways.
        </>
      ),
    },
    {
      title: 'Interactive learning',
      image: 'problem-solving-2',
      description: (
        <>
          Scratch’s visual interface and real-time model responses make learning
          intuitive and fun. Students see instant results as their AI models
          come to life through sprites and animations.
        </>
      ),
    },
    {
      title: 'Focus on problem solving and critical thinking',
      image: 'logical-thinking',
      description: (
        <>
          Whether debugging a gesture recognition error or training a model to
          classify fruits, students sharpen their logic, creativity, and
          decision-making skills vital for future success in tech and beyond.
        </>
      ),
    },
    {
      title: 'Easy accessibility',
      image: 'easy-accessibility',
      description: (
        <>
          Everything runs online! No installations required. Students get 24/7
          access to recordings, live support, and resources, so they can train,
          test, and create AI projects at their own pace.
        </>
      ),
    },
  ],
  GOOGLE_COLAB: [
    {
      title: 'Hands-On experience with industry tools',
      image: 'animation-and-motion-control',
      description: (
        <>
          Kids get to work with the same tools used by data scientists, AI
          engineers, and tech professionals. Google Colab offers an authentic
          coding environment with real-world libraries like Pandas, NumPy,
          TensorFlow, and Matplotlib, helping students build skills that go
          beyond the classroom.
        </>
      ),
    },
    {
      title: 'Build real-world projects using Google Colab',
      image: 'laptop-coding',
      description: (
        <>
          From building stock market predictors to face recognition systems,
          every project in our curriculum is crafted to reflect real-world
          problems. Students document their code, visualize outcomes, and learn
          how to debug, just like professional developers. These hands-on
          experiences build a portfolio they can be proud of.
        </>
      ),
    },
    {
      title: 'Progressive & supportive learning environment',
      image: 'analytical-thinking',
      description: (
        <>
          Our courses are designed with kids and teens in mind, starting with
          the basics and advancing gradually. Instructors provide personalized
          feedback and one-on-one support, ensuring no child is left behind.
          Mistakes are part of the learning process, and every challenge becomes
          an opportunity to grow.
        </>
      ),
    },
    {
      title: 'Flexibility and accessibility',
      image: 'setting-arrow',
      description: (
        <>
          All you need is a browser! Google Colab works on any device with
          internet access, making it easy for kids to learn from home, during
          vacations, or on the go. There’s no need to install software or worry
          about storage, just open a Colab notebook and start coding instantly.
        </>
      ),
    },
    {
      title: 'Focus on creativity and problem-solving',
      image: 'logical-thinking',
      description: (
        <>
          Our Google Colab projects encourage students to think creatively,
          explore data, and build solutions to meaningful problems. Whether
          they’re creating an AI-based movie recommender or visualizing
          pollution data, kids develop both technical skills and the mindset to
          solve real challenges in the world around them.
        </>
      ),
    },
  ],
  NODE_JS: [
    {
      title: 'Hands-On experience with industry tools',
      image: 'animation-and-motion-control',
      description: (
        <>
          Students use industry-standard tools like Visual Studio Code, GitHub,
          and Node.js modules to build powerful backend systems and APIs. These
          tools give kids a real-world introduction to the development
          environment used by professional backend developers, laying a solid
          foundation for advanced programming and AI applications.
        </>
      ),
    },
    {
      title: 'Exciting Node.js projects',
      image: 'laptop-coding',
      description: (
        <>
          From creating their first “Hello, world!” server to working with the
          File System module to read and write files, students tackle engaging
          projects step by step. They also build database projects, learning how
          to add data using INSERT queries, retrieve data with SELECT queries,
          update existing data, and combine tables using JOIN queries. Each
          project builds confidence in handling real backend challenges.
        </>
      ),
    },
    {
      title: 'Interactive and progressive learning',
      image: 'analytical-thinking',
      description: (
        <>
          The Node.js course is structured to guide students from basic concepts
          like what Node.js is and how to create a simple server, to working
          with files using the fs module, and then to understanding databases,
          creating tables, and writing SQL queries to manage data. Through
          interactive lessons, coding activities, and live guidance, students
          build their skills progressively.
        </>
      ),
    },
    {
      title: 'Flexibility and accessibility',
      image: 'setting-arrow',
      description: (
        <>
          With live online classes, session recordings, and mentor support,
          students can learn Node.js at their own pace. Whether revisiting file
          operations or practicing new database queries, the course supports all
          learning styles and schedules, ensuring each student masters backend
          concepts confidently.
        </>
      ),
    },
    {
      title: 'Focus on creativity and problem-solving',
      image: 'logical-thinking',
      description: (
        <>
          Students are encouraged to think creatively, whether it’s designing
          how to organise files on a server, planning database tables for an
          application, or writing queries to manage and connect data
          efficiently. By working on real backend tasks, they not only learn to
          code but also develop critical problem-solving, logical thinking, and
          system design skills needed for future projects.
        </>
      ),
    },
  ],
  VISUAL_STUDIO_CODE: [
    {
      title: 'Hands-on experience with industry tools',
      image: 'coding-tools',
      description: (
        <>
          Students learn to create web pages, interactive apps, and code their
          own projects using Visual Studio Code, one of the most widely-used and
          trusted tools in the programming industry. With its beginner-friendly
          interface, Visual Studio Code allows students to gain real-world
          coding experience while building practical coding projects. This
          hands-on experience lays the foundation for future web development and
          backend programming, giving students the confidence they need to
          create amazing digital experiences.
        </>
      ),
    },
    {
      title: 'Exciting Visual Studio Code projects',
      image: 'design-thinking',
      description: (
        <>
          <p>
            Students will dive into a variety of creative projects using Visual
            Studio Code, such as:
          </p>
          <ul>
            <li>Personal bio websites</li>
            <li>Digital storybooks</li>
            <li>Interactive quizzes and games</li>
          </ul>
          <p>
            These fun projects help kids understand how websites and
            applications are structured, and provide them with the opportunity
            to bring their creative ideas to life through coding. Students will
            get hands-on experience in real-world programming, learning how to
            organize content, build functionality, and style their digital
            creations in Visual Studio Code.
          </p>
        </>
      ),
    },
    {
      title: 'Interactive and progressive learning',
      image: 'problem-solving-2',
      description: (
        <>
          <p>
            Our curriculum is designed to grow with your child’s coding
            abilities. It starts with basic tags and simple coding concepts in
            Visual Studio Code and moves toward more complex page structures and
            interactive elements. Along the way, students get to:
          </p>
          <ul>
            <li>Build functional web pages</li>
            <li>Apply their coding knowledge to creative projects</li>
            <li>
              Receive instant feedback through quizzes and live coding
              challenges
            </li>
          </ul>
          <p>
            Learning to code in Visual Studio Code has never been more fun,
            engaging, or accessible. Our interactive lessons keep students
            motivated and excited about coding.
          </p>
        </>
      ),
    },
    {
      title: 'Focus on Creativity and Problem-Solving',
      image: 'logical-thinking',
      description: (
        <>
          <p>
            Visual Studio Code isn’t just about coding; it’s about creative
            expression and problem-solving. Students use VS Code to:
          </p>
          <ul>
            <li>Organize content creatively</li>
            <li>Troubleshoot code and formatting issues</li>
            <li>Structure websites and apps logically</li>
            <li>Build sites that are both functional and engaging</li>
          </ul>
          <p>
            This hands-on learning approach ensures that students don’t just
            learn to code, but think critically, solve problems, and express
            themselves creatively through their projects—skills essential for
            future innovators!
          </p>
        </>
      ),
    },
    {
      title: 'Flexibility and accessibility',
      image: 'easy-accessibility',
      description: (
        <>
          With live classes and recorded lessons, students can learn Visual
          Studio Code at their own pace, anytime, anywhere. Whether they’re
          revisiting a concept, exploring a new feature, or trying out a coding
          challenge, students have round-the-clock access to resources, making
          it easy for busy families to fit learning into their schedules.
        </>
      ),
    },
  ],
  SQL: [
    {
      title: 'Hands-on experience with industry tools',
      image: 'coding-tools',
      description: (
        <>
          Students will work with industry-standard tools and real-world
          datasets, allowing them to apply their SQL knowledge to solve
          practical problems. This hands-on experience prepares them for future
          careers in data science, AI, and machine learning, where SQL plays a
          critical role in data management and analysis.
        </>
      ),
    },
    {
      title: 'Exciting SQL projects',
      image: 'design-thinking',
      description: (
        <>
          Students will build projects that showcase their SQL skills, from
          creating databases and performing queries to working with Python for
          data analysis. These exciting, real-world projects help students apply
          their learning in practical ways while keeping them engaged and
          motivated.
        </>
      ),
    },
    {
      title: 'Interactive and progressive learning',
      image: 'problem-solving-2',
      description: (
        <>
          Our SQL modules are designed with interactive lessons and coding
          challenges to ensure that students learn at their own pace. With each
          lesson, students build on their previous knowledge and gain the
          confidence to tackle more complex problems and queries.
        </>
      ),
    },
    {
      title: 'Focus on creativity and problem-solving',
      image: 'logical-thinking',
      description: (
        <>
          Students use SQL to creatively organize and present information. They
          learn to troubleshoot formatting issues, structure content logically,
          and build sites that are both functional and engaging - essential
          skills for future innovators!
        </>
      ),
    },
    {
      title: 'Flexibility and accessibility',
      image: 'easy-accessibility',
      description: (
        <>
          All SQL lessons are conducted online, with full-time mentor support
          and 24/7 access to recorded lessons and step-by-step resources.
          Students can revisit lessons and practice coding at their own pace,
          making it easy for them to learn anytime, anywhere.
        </>
      ),
    },
  ],
  REACT_JS: [
    {
      title: 'Real-World Coding Experience',
      image: 'coding-tools',
      description: (
        <>
          Students get hands-on experience working directly with React.js and
          JavaScript, learning how React renders HTML efficiently using its
          Virtual DOM. They write real code to create interactive elements,
          manage user input, and update web pages instantly without refreshing.
          This helps them understand how real-world web applications like
          Instagram and Netflix are built.
        </>
      ),
    },
    {
      title: 'Creative projects',
      image: 'design-thinking',
      description: (
        <>
          Our React.js projects make learning exciting and practical. Kids
          design apps using components, events, props, and state, such as
          digital scoreboards, quiz apps, or color-changing dashboards. Each
          project challenges them to apply logic, creativity, and
          problem-solving while exploring modern web development concepts.
        </>
      ),
    },
    {
      title: 'Interactive learning',
      image: 'problem-solving-2',
      description: (
        <>
          Learning React.js at Codingal is active and engaging. Every class
          involves live coding sessions, guided activities, and challenges where
          students see their web apps update in real time. Lessons build
          progressively, from basic JavaScript and HTML integration to advanced
          React features that ensure students gain confidence step by step.
        </>
      ),
    },
    {
      title: 'Focus on problem solving and critical thinking',
      image: 'logical-thinking',
      description: (
        <>
          React.js teaches kids to think like real developers. They learn to
          break problems into smaller components, reuse code efficiently, and
          debug their own projects. This strengthens logical reasoning, critical
          thinking, and independent creativity, all while keeping learning fun
          and visual.
        </>
      ),
    },
    {
      title: 'Easy accessibility',
      image: 'easy-accessibility',
      description: (
        <>
          Learning React.js with Codingal is simple and flexible. Kids can code
          directly in browser-based tools like Replit, CodeSandbox, or
          StackBlitz, no installations needed. With recorded sessions, guided
          practice projects, and round-the-clock access to resources, students
          can keep learning and building amazing React apps anytime, anywhere.
        </>
      ),
    },
  ],
  GIT: [
    {
      title: 'Hands-on experience with industry tools',
      image: 'coding-tools',
      description: (
        <>
          Students learn to build web pages using GIT, the foundation of the
          internet. They use simple tools like online editors and professional
          platforms like Visual Studio Code, setting them up for future success
          in web development and AI & coding - how cool is that?!
        </>
      ),
    },
    {
      title: 'Exciting GIT projects',
      image: 'design-thinking',
      description: (
        <>
          From personal bios and digital storybooks to functional web pages,
          students create real-world projects using HTML. These projects teach
          kids how websites are structured and how they can bring their ideas to
          life on the web - imagine what they&apos;ll create!
        </>
      ),
    },
    {
      title: 'Interactive and progressive learning',
      image: 'problem-solving-2',
      description: (
        <>
          Kids follow a curriculum designed to grow with them - starting from
          basic tags to more complex page structures. Along the way, they build
          their own sites, receive instant feedback, and enjoy learning through
          quizzes and live challenges - learning has never been this fun!
        </>
      ),
    },
    {
      title: 'Focus on creativity and problem-solving',
      image: 'logical-thinking',
      description: (
        <>
          Students use GIT to creatively organize and present information. They
          learn to troubleshoot formatting issues, structure content logically,
          and build sites that are both functional and engaging - essential
          skills for future innovators!
        </>
      ),
    },
    {
      title: 'Flexibility and accessibility',
      image: 'easy-accessibility',
      description: (
        <>
          Whether attending live classes or reviewing recorded lessons, students
          can learn GIT whenever and wherever they prefer. With round-the-clock
          access to resources and instructor support, learning never has to stop
          - so convenient for busy families!
        </>
      ),
    },
  ],
  GITHUB: [
    {
      title: 'Hands-on experience with industry tools',
      image: 'coding-tools',
      description: (
        <>
          Students learn to build web pages using GitHub, the foundation of the
          internet. They use simple tools like online editors and professional
          platforms like Visual Studio Code, setting them up for future success
          in web development and AI & coding - how cool is that?!
        </>
      ),
    },
    {
      title: 'Exciting Github projects',
      image: 'design-thinking',
      description: (
        <>
          From personal bios and digital storybooks to functional web pages,
          students create real-world projects using HTML. These projects teach
          kids how websites are structured and how they can bring their ideas to
          life on the web - imagine what they&apos;ll create!
        </>
      ),
    },
    {
      title: 'Interactive and progressive learning',
      image: 'problem-solving-2',
      description: (
        <>
          Kids follow a curriculum designed to grow with them - starting from
          basic tags to more complex page structures. Along the way, they build
          their own sites, receive instant feedback, and enjoy learning through
          quizzes and live challenges - learning has never been this fun!
        </>
      ),
    },
    {
      title: 'Focus on creativity and problem-solving',
      image: 'logical-thinking',
      description: (
        <>
          Students use GitHub to creatively organize and present information.
          They learn to troubleshoot formatting issues, structure content
          logically, and build sites that are both functional and engaging -
          essential skills for future innovators!
        </>
      ),
    },
    {
      title: 'Flexibility and accessibility',
      image: 'easy-accessibility',
      description: (
        <>
          Whether attending live classes or reviewing recorded lessons, students
          can learn GitHub whenever and wherever they prefer. With
          round-the-clock access to resources and instructor support, learning
          never has to stop - so convenient for busy families!
        </>
      ),
    },
  ],
}

export const COURSE_FEATURES: Record<string, CourseFeaturesType> = {
  AI_AND_ML: {
    outcomes: [
      'ChatGPT Integration',
      'Google Gemini Integration',
      'Natural Language Processing',
      'Machine Learning',
      'Image & Speech Recognition',
      'Prompt Engineering',
      'Computer Vision (Object, Face & Gesture Detection)',
    ],
    skills: [
      {
        imagePath: '/images/courses/icons/ai-ml-core-principles',
        skillName: 'AI & ML Core Principles',
      },
      {
        imagePath: '/images/courses/icons/analytical-thinking',
        skillName: 'Analytical Thinking',
      },
      {
        imagePath: '/images/courses/icons/logic-building',
        skillName: 'Logic Building',
      },
      {
        imagePath: '/images/courses/icons/pictoblox-edublocks',
        skillName: 'Pictoblox, EduBlocks',
      },
      {
        imagePath: '/images/courses/icons/google-gemini-hugging-face-for-llms',
        skillName: 'Google Gemini, Hugging Face for LLMs',
      },
      {
        imagePath: '/images/courses/icons/ai-ml-programming',
        skillName: 'AI, ML Programming',
      },
    ],
  },
  THUNKABLE: {
    outcomes: [
      'Cross-Platform Skills',
      'Motion and Effects',
      'Platform-Specific Tools and Techniques',
      'Sound Integration',
      'Sprite and Object Manipulation',
      'User Interaction',
    ],
    skills: [
      {
        imagePath: '/images/courses/icons/node-tapping',
        skillName: 'Creative Thinking and Problem-Solving',
      },
      {
        imagePath: '/images/courses/icons/logical-reasoning',
        skillName: 'Logical Reasoning and Sequencing',
      },
      {
        imagePath: '/images/courses/icons/design-and-animate-creatively',
        skillName: 'Storytelling and Visual Design Skills',
      },
      {
        imagePath: '/images/courses/icons/animation-and-motion-control',
        skillName: 'Thunkable and Motion Control Techniques',
      },
      {
        imagePath: '/images/courses/icons/user-interaction-and-event-handling',
        skillName: 'User Interaction and Event Handling',
      },
      {
        imagePath: '/images/courses/icons/debugging-troubleshooting.png',
        skillName: 'Debugging and Troubleshooting Proficiency',
      },
    ],
  },
  JAVA: {
    outcomes: [
      'Java basics & setup',
      'Data types, operators, control flow',
      'Loops & user input',
      'Object-oriented programming (OOP)',
      'Arrays & ArrayLists',
      'Sorting, searching, recursion',
      'File handling',
    ],
    skills: [
      {
        imagePath: '/images/courses/icons/java-foundation',
        skillName: 'Foundation in Java programming',
      },
      {
        imagePath: '/images/courses/icons/complex-problem-solving',
        skillName: 'Solve complex coding challenges',
      },
      {
        imagePath: '/images/courses/icons/implement-algorithms',
        skillName: 'Implement Algorithms efficiently',
      },
      {
        imagePath: '/images/courses/icons/master-oops',
        skillName: 'Master key OOP principles',
      },
    ],
  },
  TRINKET: {
    outcomes: [
      'Cross-Platform Skills',
      'Motion and Effects',
      'Platform-Specific Tools and Techniques',
      'Sound Integration',
      'Sprite and Object Manipulation',
      'User Interaction',
    ],
    skills: [
      {
        imagePath: 'images/courses/icons/analytical-thinking',
        skillName: 'Creative Thinking and Problem-Solving',
      },
      {
        imagePath: '/images/courses/icons/logical-reasoning',
        skillName: 'Logical Reasoning and Sequencing',
      },
      {
        imagePath: '/images/courses/icons/design-thinking',
        skillName: 'Storytelling and Visual Design Skills',
      },
      {
        imagePath: '/images/courses/icons/animation-and-motion-control',
        skillName: 'Trinket and Motion Control Techniques',
      },
      {
        imagePath: '/images/courses/icons/user-interaction-and-event-handling',
        skillName: 'User Interaction and Event Handling',
      },
      {
        imagePath: '/images/courses/icons/debugging-troubleshooting',
        skillName: 'Debugging and Troubleshooting Proficiency',
      },
    ],
  },
  PYGAME: {
    outcomes: [
      'Understanding the basics of Pygame',
      'Utilizing Pygame tags to create headings, paragraphs, and lists',
      'Embedding images and videos',
      'Introduction to forms and tables',
      'Basics of structuring content for the web',
      'Confidence to explore more complex coding languages and AI tools',
    ],
    skills: [
      {
        imagePath: '/images/courses/icons/analytical-thinking',
        skillName: 'Web page structuring and design',
      },
      {
        imagePath: '/images/courses/icons/design-thinking',
        skillName: 'Utilizing core Pygame tags effectively',
      },
      {
        imagePath: '/images/courses/icons/portfolio',
        skillName: 'Building and showcasing personal web pages',
      },
      {
        imagePath: '/images/courses/icons/node-tapping',
        skillName: 'Creating links, lists, tables, and forms',
      },
      {
        imagePath: '/images/courses/icons/animation-and-motion-control',
        skillName: 'Embedding media content (images, videos, audio)',
      },
      {
        imagePath: '/images/courses/icons/coding',
        skillName: 'Attention to detail in writing clean, structured code',
      },
    ],
  },
  ANIMATION: {
    outcomes: [
      'Cross-Platform Skills',
      'Motion and Effects',
      'Platform-Specific Tools and Techniques',
      'Sound Integration',
      'Sprite and Object Manipulation',
      'User Interaction',
    ],
    skills: [
      {
        imagePath: '/images/courses/icons/analytical-thinking',
        skillName: 'Creative Thinking and Problem-Solving',
      },
      {
        imagePath: '/images/courses/icons/logical-reasoning',
        skillName: 'Logical Reasoning and Sequencing',
      },
      {
        imagePath: '/images/courses/icons/design-thinking',
        skillName: 'Storytelling and Visual Design Skills',
      },
      {
        imagePath: '/images/courses/icons/animation-and-motion-control',
        skillName: 'Animation and Motion Control Techniques',
      },
      {
        imagePath: '/images/courses/icons/user-interaction-and-event-handling',
        skillName: 'User Interaction and Event Handling',
      },
      {
        imagePath: '/images/courses/icons/debugging-troubleshooting',
        skillName: 'Debugging and Troubleshooting Proficiency',
      },
    ],
  },
  TURTLE: {
    outcomes: [
      'Cross-Platform Skills',
      'Motion and Effects',
      'Platform-Specific Tools and Techniques',
      'Sound Integration',
      'Sprite and Object Manipulation',
      'User Interaction',
    ],
    skills: [
      {
        imagePath: '/images/courses/icons/node-tapping',
        skillName: 'Creative Thinking and Problem-Solving',
      },
      {
        imagePath: '/images/courses/icons/logical-reasoning',
        skillName: 'Logical Reasoning and Sequencing',
      },
      {
        imagePath: '/images/courses/icons/design-and-animate-creatively',
        skillName: 'Storytelling and Visual Design Skills',
      },
      {
        imagePath: '/images/courses/icons/animation-and-motion-control',
        skillName: 'Python Turtle and Motion Control Techniques',
      },
      {
        imagePath: '/images/courses/icons/user-interaction-and-event-handling',
        skillName: 'User Interaction and Event Handling',
      },
      {
        imagePath: '/images/courses/icons/debugging-troubleshooting.png',
        skillName: 'Debugging and Troubleshooting Proficiency',
      },
    ],
  },

  MIT_APP_INVENTOR: {
    outcomes: [
      'Basics of mobile app development',
      'How to use MIT App Inventor’s drag-and-drop blocks',
      'Creating app screens, buttons, forms, and actions',
      'Designing interactive user interfaces',
      'Introductory AI features through App Inventor extensions',
      'Develop design and problem-solving skills',
    ],
    skills: [
      {
        imagePath: '/images/courses/icons/analytical-thinking',
        skillName: 'Logic-Based Thinking',
      },
      {
        imagePath: '/images/courses/icons/mobile-app',
        skillName: 'Mobile UI/UX Design',
      },
      {
        imagePath: '/images/courses/icons/coding',
        skillName: 'Event-Driven Programming',
      },
      {
        imagePath: '/images/courses/icons/debugging',
        skillName: 'Debugging and Testing',
      },
      {
        imagePath: '/images/courses/icons/ai-and-coding',
        skillName: 'AI & Coding Foundations',
      },
      {
        imagePath: '/images/courses/icons/android-app',
        skillName: 'Create Fully Functional Android Apps',
      },
    ],
  },

  BOOTSTRAP: {
    outcomes: [
      'Understanding the basics of Bootstrap',
      'Utilizing Bootstrap tags to create headings, paragraphs, and lists',
      'Embedding images and videos',
      'Introduction to forms and tables',
      'Basics of structuring content for the web',
      'Confidence to explore more complex coding languages and AI tools',
    ],
    skills: [
      {
        imagePath: '/images/courses/icons/analytical-thinking',
        skillName: 'Web page structuring and design',
      },
      {
        imagePath: '/images/courses/icons/design-thinking',
        skillName: 'Utilizing core Bootstrap tags effectively',
      },
      {
        imagePath: '/images/courses/icons/portfolio',
        skillName: 'Building and showcasing personal web pages',
      },
      {
        imagePath: '/images/courses/icons/node-tapping',
        skillName: 'Creating links, lists, tables, and forms',
      },
      {
        imagePath: '/images/courses/icons/animation-and-motion-control',
        skillName: 'Embedding media content (images, videos, audio)',
      },
      {
        imagePath: '/images/courses/icons/coding',
        skillName: 'Attention to detail in writing clean, structured code',
      },
    ],
  },
  JAVASCRIPT: {
    outcomes: [
      'Javascript, HTML, CSS Introduction',
      'Syntex Guide, Comments, objects, Variable',
      'Platform-Specific Tools and Techniques',
      'Array, Method, Call Stack, Loop, Switch, Functions',
      'About Javascript, Data Types, keywords, conditional statements',
      'Class, inheritance, array sorting, regular expressions, Json call back.',
      'Async, API, Cookie',
    ],
    skills: [
      {
        imagePath: '/images/courses/icons/node-tapping',
        skillName: 'Creative Thinking and Problem-Solving',
      },
      {
        imagePath: '/images/courses/icons/logical-reasoning',
        skillName: 'Logical Reasoning and Sequencing',
      },
      {
        imagePath: '/images/courses/icons/web-development',
        skillName: 'Introduction to web development',
      },
      {
        imagePath: '/images/courses/icons/java-foundation',
        skillName: 'Advance Javascript concept ',
      },
      {
        imagePath: '/images/courses/icons/user-interaction-and-event-handling',
        skillName: 'User Interaction and Event Handling',
      },
      {
        imagePath: '/images/courses/icons/debugging-troubleshooting.png',
        skillName: 'Debugging and Troubleshooting Proficiency',
      },
    ],
  },
  CSS: {
    outcomes: [
      'Applying colors, backgrounds, and fonts to web pages',
      'Using text styling properties like size, alignment, and spacing',
      'Controlling layout with margins, borders, and padding (Box Model)',
      'Selecting and targeting HTML elements with precision using selectors',
      'Enhancing user interaction with pseudo-classes and pseudo-elements',
      'Building confidence to integrate CSS with HTML and explore responsive design',
    ],
    skills: [
      {
        imagePath: '/images/courses/icons/analytical-thinking',
        skillName: 'Styling and designing structured web pages',
      },
      {
        imagePath: '/images/courses/icons/design-thinking',
        skillName: 'Applying consistent themes across multiple elements',
      },
      {
        imagePath: '/images/courses/icons/animation-and-motion-control',
        skillName: 'Understanding the visual flow and layout of content',
      },
      {
        imagePath: '/images/courses/icons/node-tapping',
        skillName: 'Using developer tools to inspect and tweak styles',
      },
      {
        imagePath: '/images/courses/icons/coding',
        skillName: 'Building real-world web pages with custom styling',
      },
      {
        imagePath: '/images/courses/icons/portfolio',
        skillName: 'Creating visually engaging project portfolios',
      },
    ],
  },
  HTML: {
    outcomes: [
      'Understanding the structure of web pages',
      'Utilizing HTML tags to create headings, paragraphs, and lists',
      'Embedding images and videos',
      'Introduction to forms and tables',
      'Basics of structuring content for the web',
      'Confidence to explore more complex coding languages and AI tools',
    ],
    skills: [
      {
        imagePath: '/images/courses/icons/analytical-thinking',
        skillName: 'Web page structuring and design',
      },
      {
        imagePath: '/images/courses/icons/design-thinking',
        skillName: 'Utilizing core HTML tags effectively',
      },
      {
        imagePath: '/images/courses/icons/animation-and-motion-control',
        skillName: 'Embedding media content (images, videos, audio)',
      },
      {
        imagePath: '/images/courses/icons/node-tapping',
        skillName: 'Creating links, lists, tables, and forms',
      },
      {
        imagePath: '/images/courses/icons/coding',
        skillName: 'Attention to detail in writing clean, structured code',
      },
      {
        imagePath: '/images/courses/icons/portfolio',
        skillName: 'Building and showcasing personal web pages',
      },
    ],
  },
  AICODE101: {
    outcomes: [
      'Introduction to AI with Scratch + Teachable Machine',
      'Image recognition and emotion detection',
      'Chatbot and conversational AI creation',
      'Voice-activated games and audio processing',
      'Face recognition and security systems',
      'Language translation and communication tools',
      'Problem-solving through real-world AI challenges',
    ],
    skills: [
      {
        imagePath: '/images/courses/icons/java-foundation',
        skillName: 'Block-based programming with AI integration',
      },
      {
        imagePath: '/images/courses/icons/ai-training',
        skillName: 'AI model training and testing',
      },
      {
        imagePath: '/images/courses/icons/chatbot',
        skillName: 'Chatbot development and conversational AI',
      },
      {
        imagePath: '/images/courses/icons/ai-voice',
        skillName: 'Voice processing and audio recognition',
      },
      {
        imagePath: '/images/courses/icons/ai-translation',
        skillName: 'Language translation and pattern recognition',
      },
      {
        imagePath: '/images/courses/icons/image-recognition',
        skillName: 'Image recognition and emotion detection',
      },
    ],
  },
  CODE_ORG: {
    outcomes: [
      'Block-based coding fundamentals',
      'Animation and game creation',
      'App development skills',
      'AI-powered coding experiences',
      'Problem-solving through challenges',
      'Turtle graphics and drawing',
      'Interactive storytelling techniques',
    ],
    skills: [
      {
        imagePath: '/images/courses/icons/java-foundation',
        skillName: 'Block-based programming mastery',
      },
      {
        imagePath: '/images/courses/icons/animation-and-motion-control',
        skillName: 'Animation and game development',
      },
      {
        imagePath: '/images/courses/icons/implement-algorithms',
        skillName: 'Problem-solving and logical thinking',
      },
      {
        imagePath: '/images/courses/icons/ai',
        skillName: 'AI integration techniques',
      },
      {
        imagePath: '/images/courses/icons/node-tapping',
        skillName: 'App building fundamentals',
      },
      {
        imagePath: '/images/courses/icons/complex-thinking',
        skillName: 'Computational thinking skills',
      },
    ],
  },
  PICTOBLOX: {
    outcomes: [
      'PictoBlox setup and block-based AI coding',
      'Face, expression, and object detection',
      'Obstacle detection in self-driving cars',
      'Smart translator with speech output',
      'AI-powered yoga pose detection',
      'ChatGPT: ask, translate, and create with AI',
    ],
    skills: [
      {
        imagePath: '/images/courses/icons/ai-fundamentals',
        skillName: 'AI fundamentals through hands-on projects',
      },
      {
        imagePath: '/images/courses/icons/chatgpt',
        skillName: 'Basic understanding of ChatGPT and generative AI',
      },
      {
        imagePath: '/images/courses/icons/ai-play-games',
        skillName: 'Designing interactive AI games and animations',
      },
      {
        imagePath: '/images/courses/icons/tech',
        skillName: 'Improving digital literacy and tech fluency',
      },
    ],
  },
  TEACHABLE_MACHINE: {
    outcomes: [
      'Training AI models using Google’s Teachable Machine',
      'Recognizing images, sounds, gestures, and poses',
      'Integrating trained models into Scratch via MIT Raise Playground',
      'Building interactive AI games and smart applications',
      'Understanding basic AI concepts: classification, input/output, model confidence',
      'Applying machine learning in creative, real-world scenarios',
    ],
    skills: [
      {
        imagePath: '/images/courses/icons/ai-ml-fundamentals',
        skillName: 'AI and machine learning fundamentals',
      },
      {
        imagePath: '/images/courses/icons/hands-on-experience',
        skillName:
          'Hands-on experience with image, sound, and pose recognition',
      },
      {
        imagePath: '/images/courses/icons/implement-algorithms',
        skillName: 'Integrating AI models using MIT Raise Playground',
      },
      {
        imagePath: '/images/courses/icons/master-oops',
        skillName: 'Real-world application of coding and AI concepts',
      },
    ],
  },
  GOOGLE_COLAB: {
    outcomes: [
      'How to write and execute Python code using Google Colab',
      'How to visualize data using libraries like Matplotlib and Seaborn',
      'Build AI-powered applications like chatbots and recommendation engines',
      'How to collaborate on code and document their thought process using Colab notebooks',
      'How to use real-world datasets and apply coding to solve practical problems',
    ],
    skills: [
      {
        imagePath: '/images/courses/icons/analytical-thinking',
        skillName: 'Python programming fundamentals',
      },
      {
        imagePath: '/images/courses/icons/animation-and-motion-control',
        skillName: 'Data visualization and storytelling with charts',
      },
      {
        imagePath: '/images/courses/icons/node-tapping',
        skillName: 'Machine learning model development',
      },
      {
        imagePath: '/images/courses/icons/ai',
        skillName: 'Cloud-based coding and file management',
      },
      {
        imagePath: '/images/courses/icons/sql',
        skillName: 'Logical reasoning and debugging',
      },
      {
        imagePath: '/images/courses/icons/planning',
        skillName: 'Real-world project planning and execution',
      },
    ],
  },
  NODE_JS: {
    outcomes: [
      'Understanding what Node.js is and how it works in backend development',
      'Using the File System (fs) module to create, read, write, and update files',
      'Learning about databases and tables, including rows, columns, and keys',
      'Writing Insert and Select SQL queries to add and fetch data',
      'Writing Update and Join SQL queries to modify and combine data from tables',
    ],
    skills: [
      {
        imagePath: '/images/courses/icons/node-tapping',
        skillName: 'Node.js basics and file handling proficiency',
      },
      {
        imagePath: '/images/courses/icons/animation-and-motion-control',
        skillName: 'Building simple backend systems with files and data',
      },
      {
        imagePath: '/images/courses/icons/implement-algorithms',
        skillName: 'Enhanced problem-solving and logical thinking abilities',
      },
      {
        imagePath: '/images/courses/icons/ai',
        skillName: 'Confidence for full-stack development and AI learning',
      },
    ],
  },
  VISUAL_STUDIO_CODE: {
    outcomes: [
      'How to code using Visual Studio Code (VS Code)',
      'Key programming concepts: loops, variables, and logic',
      'How to run, test, and debug simple programs',
      'Create mini-projects and apps',
      'Gain a solid coding base',
      'Earn STEM-accredited certificates',
    ],
    skills: [
      {
        imagePath: '/images/courses/icons/analytical-thinking',
        skillName: 'Coding foundations for future growth',
      },
      {
        imagePath: '/images/courses/icons/logical-reasoning',
        skillName: 'Strengthen problem-solving and logical thinking',
      },
      {
        imagePath: '/images/courses/icons/design-thinking',
        skillName: 'Build confidence with developer tools',
      },
      {
        imagePath: '/images/courses/icons/user-interaction-and-event-handling',
        skillName: 'Establish a strong coding foundation',
      },
      {
        imagePath: '/images/courses/icons/animation-and-motion-control',
        skillName: 'Complete real-world projects',
      },
      {
        imagePath: '/images/courses/icons/debugging-troubleshooting',
        skillName: 'Explore advanced designs',
      },
    ],
  },
  SQL: {
    outcomes: [
      'Introduction to creating databases and tables using SQL commands',
      'Understanding operators and how to filter and compare data',
      'Learn how to summarize data using functions like COUNT, SUM, AVG, etc',
      'Master techniques to sort and filter data to extract useful insights',
      'Apply all SQL skills to build real-world data projects and establish a strong foundation',
    ],
    skills: [
      {
        imagePath: '/images/courses/icons/financial-database',
        skillName: 'Learn how to create and manage databases',
      },
      {
        imagePath: '/images/courses/icons/animation-and-motion-control',
        skillName: 'Become proficient in writing complex SQL queries',
      },
      {
        imagePath: '/images/courses/icons/node-tapping',
        skillName: 'Learn how to clean, filter, and sort data for analysis',
      },
      {
        imagePath: '/images/courses/icons/sql',
        skillName: 'Understand how SQL is used in data-driven industries',
      },
    ],
  },
  REACT_JS: {
    outcomes: [
      'Introduction to React',
      'React Render HTML',
      'Javascript',
      'Events in React',
      'Props in React',
      'State in React',
    ],
    skills: [
      {
        imagePath: '/images/courses/icons/problem-solving-2',
        skillName: 'Creative Thinking and Problem-Solving',
      },
      {
        imagePath: '/images/courses/icons/logical-reasoning',
        skillName: 'Logical Reasoning and Sequencing',
      },
      {
        imagePath: '/images/courses/icons/web-development',
        skillName: 'Introduction to web development',
      },
      {
        imagePath: '/images/courses/icons/react',
        skillName: 'Advance React.js concept',
      },
      {
        imagePath: '/images/courses/icons/user-interaction-and-event-handling',
        skillName: 'User Interaction and Event Handling',
      },
      {
        imagePath: '/images/courses/icons/debugging-troubleshooting',
        skillName: 'Debugging and Troubleshooting Proficiency',
      },
    ],
  },
  GIT: {
    outcomes: [
      'Understanding the basic of GIT',
      'Utilizing GIT to collaborate with teams',
      'GIT installation and setup',
      'Create your first repository',
      'Tracking Changes, merge the code',
      'Confidence to explore more complex coding languages and AI tools',
    ],
    skills: [
      {
        imagePath: '/images/courses/icons/problem-solving-2',
        skillName: 'Installation of GIT',
      },
      {
        imagePath: '/images/courses/icons/logical-reasoning',
        skillName: 'Utilizing GIT effectively',
      },
      {
        imagePath: '/images/courses/icons/web-development',
        skillName:
          'Building and showcasing personal web pages with different version control.',
      },
      {
        imagePath: '/images/courses/icons/user-interaction-and-event-handling',
        skillName: 'Create repository, merge code, save changes',
      },
      {
        imagePath: '/images/courses/icons/debugging-troubleshooting',
        skillName: 'Attention to detail in writing clean, structured code',
      },
    ],
  },
  GITHUB: {
    outcomes: [
      'Understanding the basic of Github',
      'Utilizing Github to collaborate with teams',
      'Github installation and setup',
      'Create your first repository',
      'Tracking Changes, merge the code',
      'Confidence to explore more complex coding languages and AI tools',
    ],
    skills: [
      {
        imagePath: '/images/courses/icons/problem-solving-2',
        skillName: 'Installation of Github',
      },
      {
        imagePath: '/images/courses/icons/logical-reasoning',
        skillName: 'Utilizing Github effectively',
      },
      {
        imagePath: '/images/courses/icons/web-development',
        skillName:
          'Building and showcasing personal web pages with different version control.',
      },
      {
        imagePath: '/images/courses/icons/user-interaction-and-event-handling',
        skillName: 'Create repository, merge code, save changes',
      },
      {
        imagePath: '/images/courses/icons/debugging-troubleshooting',
        skillName: 'Attention to detail in writing clean, structured code',
      },
    ],
  },
  PUBLIC_SPEAKING_GRADE_3_5: {
    outcomes: [
      'Clear, well-structured speeches',
      'Confident presence & audience awareness',
      'Voice modulation (Pace, pause, tone)',
      'Persuasive speaking with organized arguments',
      'Debate basics, including rebuttals',
      'Logical reasoning and idea development',
      'Storytelling with purpose and impact',
      'Research-based presentation techniques',
    ],
    skills: [
      {
        imagePath: '/images/courses/icons/confidence',
        skillName: 'Confident public speaking across academic settings',
      },
      {
        imagePath: '/images/courses/icons/sales-idea',
        skillName: 'Strong reasoning and argument skills',
      },
      {
        imagePath: '/images/courses/icons/complex-problem-solving',
        skillName: 'Clear and organized thinking under pressure',
      },
      {
        imagePath: '/images/courses/icons/storytelling',
        skillName: 'Effective audience engagement techniques',
      },
      {
        imagePath: '/images/courses/icons/speech',
        skillName: 'Confident leadership communication',
      },
      {
        imagePath: '/images/courses/icons/listening',
        skillName: 'Active listening and analytical response skills',
      },
    ],
  },
  PUBLIC_SPEAKING_GRADE_1_2: {
    outcomes: [
      'Clear and confident self-introductions',
      'Complete, meaningful sentences',
      'Posture, eye contact, simple gestures',
      'Voice control (loud/soft, fast/slow)',
      'Clear emotional expression',
      'Simple talk structure',
      'Opinions with basic reasons',
      'Guided group discussions',
    ],
    skills: [
      {
        imagePath: '/images/courses/icons/confidence',
        skillName: 'Comfort speaking in front of others',
      },
      {
        imagePath: '/images/courses/icons/listening',
        skillName: 'Better listening and polite response habits',
      },
      {
        imagePath: '/images/courses/icons/speech',
        skillName: 'Clear pronunciation and expressive delivery',
      },
      {
        imagePath: '/images/courses/icons/sales-idea',
        skillName: 'Better sentence formation and vocabulary',
      },
      {
        imagePath: '/images/courses/icons/storytelling',
        skillName: 'Confidence to share ideas independently',
      },
      {
        imagePath: '/images/courses/icons/speech',
        skillName: 'Calm handling of stage nervousness',
      },
    ],
  },
  CODING_GRADE_1_3: {
    outcomes: [
      'First steps in coding with visual blocks',
      'Sequencing, events, and simple logic',
      'Loops, patterns, and interactive actions',
      'Beginner game and animation creation',
      'Scratch-based storytelling and projects',
      'Early AI and app exploration',
      'Solve coding challenges step by step',
      'Problem-solving through hands-on activities',
    ],
    skills: [
      {
        imagePath: '/images/courses/icons/logical-thinking',
        skillName: 'Confidence using technology creatively',
      },
      {
        imagePath: '/images/courses/icons/sales-idea',
        skillName: 'Early sequencing and logic skills',
      },
      {
        imagePath: '/images/courses/icons/problem-solving-2',
        skillName: 'Problem-solving through guided challenges',
      },
      {
        imagePath: '/images/courses/icons/game-development-creative-design',
        skillName: 'Creative thinking through games and stories',
      },
      {
        imagePath: '/images/courses/icons/implement-algorithms',
        skillName: 'Pattern recognition and instructional thinking',
      },
      {
        imagePath: '/images/courses/icons/projects',
        skillName: 'Confidence to build and share projects',
      },
    ],
  },
  CODING_GRADE_6_8: {
    outcomes: [
      'Python and JavaScript fundamentals',
      'Building and designing websites',
      'Beginner AI and machine learning',
      'Data science and pattern spotting',
      'App development with MIT App Inventor',
      'Solving complex coding challenges',
      'Debugging and fixing real programs',
      'Building hands-on coding projects',
    ],
    skills: [
      {
        imagePath: '/images/courses/icons/logical-thinking',
        skillName: 'Creative problem-solving at a higher level',
      },
      {
        imagePath: '/images/courses/icons/sales-idea',
        skillName: 'Real Python, web, and app development skills',
      },
      {
        imagePath: '/images/courses/icons/problem-solving-2',
        skillName: 'Ability to design and build independently',
      },
      {
        imagePath: '/images/courses/icons/game-development-creative-design',
        skillName: 'Stronger analytical and logical thinking',
      },
      {
        imagePath: '/images/courses/icons/implement-algorithms',
        skillName: 'Readiness for advanced high school coding',
      },
      {
        imagePath: '/images/courses/icons/projects',
        skillName: 'Early understanding of AI and data science',
      },
    ],
  },
  CODING_GRADE_9_10: {
    outcomes: [
      'Advanced Python for apps and automation',
      'Website development with HTML, CSS, JavaScript',
      'Data science and machine learning fundamentals',
      'AI concepts and real-world applications',
      'Java programming and problem solving',
      'Competitive programming challenges',
      'Building a real project portfolio',
      'Debugging and optimizing complex programs',
    ],
    skills: [
      {
        imagePath: '/images/courses/icons/logical-thinking',
        skillName: 'Advanced logical and computational thinking',
      },
      {
        imagePath: '/images/courses/icons/sales-idea',
        skillName: 'Confidence tackling real-world coding problems',
      },
      {
        imagePath: '/images/courses/icons/problem-solving-2',
        skillName: 'Ability to work across multiple languages',
      },
      {
        imagePath: '/images/courses/icons/game-development-creative-design',
        skillName: 'Strong data literacy and analytical skills',
      },
      {
        imagePath: '/images/courses/icons/implement-algorithms',
        skillName: 'Independent project design and execution',
      },
      {
        imagePath: '/images/courses/icons/projects',
        skillName: 'Readiness for college-level computer science',
      },
    ],
  },
  CODING_GRADE_11_12: {
    outcomes: [
      'Advanced AI and machine learning',
      'Data science with real datasets',
      'Competitive programming advanced level',
      'Full stack web development',
      'Advanced Python and professional coding',
      'Portfolio building for college applications',
      'Specialized coding certifications',
      'Industry-relevant problem solving',
    ],
    skills: [
      {
        imagePath: '/images/courses/icons/logical-thinking',
        skillName: 'Professional coding across languages',
      },
      {
        imagePath: '/images/courses/icons/sales-idea',
        skillName: 'Advanced data analysis and modeling',
      },
      {
        imagePath: '/images/courses/icons/problem-solving-2',
        skillName: 'Competitive and algorithmic thinking',
      },
      {
        imagePath: '/images/courses/icons/game-development-creative-design',
        skillName: 'Independent project leadership',
      },
      {
        imagePath: '/images/courses/icons/implement-algorithms',
        skillName: 'Portfolio ready for college applications',
      },
      {
        imagePath: '/images/courses/icons/projects',
        skillName: 'Confidence to contribute in tech careers',
      },
    ],
  },
  CODING_GRADE_4_5: {
    outcomes: [
      'Build interactive apps and beginner games',
      'Design logic games and Scratch projects',
      'Create animations and interactive stories',
      'Learn the basics of website building',
      'Write beginner Python code',
      'Explore AI and machine learning concepts',
      'Solve guided coding challenges',
      'Build real projects step by step',
    ],
    skills: [
      {
        imagePath: '/images/courses/icons/logical-thinking',
        skillName: 'Confidence applying code to real projects',
      },
      {
        imagePath: '/images/courses/icons/sales-idea',
        skillName: 'Stronger logical and sequential thinking',
      },
      {
        imagePath: '/images/courses/icons/problem-solving-2',
        skillName: 'Creative problem-solving through building',
      },
      {
        imagePath: '/images/courses/icons/game-development-creative-design',
        skillName: 'Improved understanding of how technology works',
      },
      {
        imagePath: '/images/courses/icons/implement-algorithms',
        skillName: 'Ability to work through challenges independently',
      },
      {
        imagePath: '/images/courses/icons/projects',
        skillName: 'Readiness for more advanced programming',
      },
    ],
  },
}

export const COURSE_INFO: Record<string, CourseInfoType> = {
  cgm: {
    title: <Trans>AI & Coding Grandmaster: Master Coding Skills</Trans>,
    titleCompactView: <Trans>AI & Coding Grandmaster</Trans>,
    grade: '1-10',
    activites: 350,
    lessons: 144,
    duration: '12-18',
    Age: '5-15',
    description: defineMessage({
      message: `Explore 144 interactive lessons to become a master of AI and coding. Choose a guided path or personalize your journey.`,
    }),
    image: 'coding-grandmaster',
    altText: 'Online Coding Course for Kids & Teens to Master Coding Skills',
    outcomes: [
      defineMessage({ message: 'Build apps, games, and websites' }),
      defineMessage({ message: 'Master AI and coding skills' }),
      defineMessage({ message: 'Engage in project-based learning' }),
      defineMessage({
        message:
          'Earn an AI & Coding Grandmaster certificate, accredited by STEM.org',
      }),
    ],
    courseCode: 'cgm',
    curriculumPDFLink: '',
    trialCourseQuery: 'coding',
    isBestSeller: true,
    coursePageUrl: COURSE_PAGE_URL.codingGrandMaster,
    gradeOptions: [1, 2, 3, 4, 5, 6, 7, 8, 9, 10],
    isAICourse: true,
    curriculumVideos: [
      {
        grades: '1-3',
        url:
          'https://iframe.mediadelivery.net/embed/239253/15077303-b4f6-4d34-b1b3-334738e09376?autoplay=true&loop=false&muted=false&preload=true&responsive=true',
        altText: 'AI & Coding Grandmaster Course Video for Grade 1-3',
        image: 'cgm-1-3',
      },
      {
        grades: '4-5',
        url:
          'https://iframe.mediadelivery.net/embed/239253/4f10eb63-41fe-40b3-991f-b2d8be065efe?autoplay=true&loop=false&muted=false&preload=true&responsive=true',

        altText: 'AI & Coding Grandmaster Course Video for Grade 4-5',
        image: 'cgm-4-5',
      },
      {
        grades: '6-8',
        url:
          'https://iframe.mediadelivery.net/embed/239253/52b47be1-02ba-42d1-be12-51dace30f31b?autoplay=true&loop=false&muted=false&preload=true&responsive=true',
        altText: 'AI & Coding Grandmaster Course Video for Grade 6-8',
        image: 'cgm-6-8',
      },
      {
        grades: '9-10',
        url:
          'https://iframe.mediadelivery.net/embed/239253/3a0664ee-e6aa-434f-a6ce-2a4f27f4f36f?autoplay=true&loop=false&muted=false&preload=true&responsive=true',
        altText: 'AI & Coding Prodigy Course Video for Grade 9-10',
        image: 'cgm-9-10',
      },
    ],
  },
  cp: {
    title: <Trans>AI & Coding Prodigy: Code Like a Pro</Trans>,
    titleCompactView: <Trans>AI & Coding Prodigy</Trans>,
    grade: '1-12',
    activites: 184,
    lessons: 96,
    duration: '9-12',
    Age: '5-17',
    description: defineMessage({
      message:
        'A 96-lesson curriculum for kids and teens to master AI, coding, and real-world applications through projects.',
    }),
    image: 'coding-prodigy',
    altText: 'Online Coding Course for Kids & Teens to Code Like a Pro',
    outcomes: [
      defineMessage({ message: 'Learn to code like a pro' }),
      defineMessage({ message: 'Create amazing apps and games' }),
      defineMessage({
        message:
          'Earn an AI & Coding Prodigy certificate (STEM.org Accredited)',
      }),
      defineMessage({ message: 'Master problem-solving skills' }),
    ],
    courseCode: 'cp',
    curriculumPDFLink: '',
    trialCourseQuery: 'coding',
    isBestSeller: false,
    coursePageUrl: COURSE_PAGE_URL.codingProdigy,
    gradeOptions: [1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12],
    isAICourse: true,
    curriculumVideos: [
      {
        grades: '1-3',
        url:
          'https://iframe.mediadelivery.net/embed/239253/15077303-b4f6-4d34-b1b3-334738e09376?autoplay=true&loop=false&muted=false&preload=true&responsive=true',
        altText: 'AI & Coding Prodigy Course Video for Grade 1-3',
        image: 'cgm-1-3',
      },
      {
        grades: '4-5',
        url:
          'https://iframe.mediadelivery.net/embed/239253/4f10eb63-41fe-40b3-991f-b2d8be065efe?autoplay=true&loop=false&muted=false&preload=true&responsive=true',
        altText: 'AI & Coding Prodigy Course Video for Grade 4-5',
        image: 'cgm-4-5',
      },
      {
        grades: '6-8',
        url:
          'https://iframe.mediadelivery.net/embed/239253/52b47be1-02ba-42d1-be12-51dace30f31b?autoplay=true&loop=false&muted=false&preload=true&responsive=true',
        altText: 'AI & Coding Prodigy Course Video for Grade 6-8',
        image: 'cgm-6-8',
      },
      {
        grades: '9-10',
        url:
          'https://iframe.mediadelivery.net/embed/239253/3a0664ee-e6aa-434f-a6ce-2a4f27f4f36f?autoplay=true&loop=false&muted=false&preload=true&responsive=true',
        altText: 'AI & Coding Prodigy Course Video for Grade 9-10',
        image: 'cgm-9-10',
      },
      {
        grades: '11-12',
        url:
          'https://iframe.mediadelivery.net/embed/239253/649ee9e2-668a-4bc4-9a79-c7e92632d3bb?autoplay=true&loop=false&muted=false&preload=true&responsive=true',
        altText: 'AI & Coding Prodigy Course Video for Grade 11-12',
        image: 'cgm-11-12',
      },
    ],
  },
  cc: {
    title: <Trans>AI & Coding Champion: Learn to Code</Trans>,
    titleCompactView: <Trans>AI & Coding Champion</Trans>,
    grade: '1-12',
    activites: 50,
    lessons: 48,
    duration: '4-6',
    Age: '5-17',
    description: defineMessage({
      message:
        'Kickstart AI & coding for kids and teens with 48 fun lessons and hands-on projects that build skills and creativity.',
    }),
    image: 'coding-champion',
    altText: 'Coding Course for Kids & Teens to Learn to Code',
    outcomes: [
      defineMessage({ message: 'Kickstart your AI & coding journey' }),
      defineMessage({ message: 'Build amazing apps, games, & websites' }),
      defineMessage({
        message:
          'Earn an AI & Coding Champion Certificate (STEM.org Accredited)',
      }),
      defineMessage({ message: 'Unlock problem-solving superpowers' }),
    ],
    courseCode: 'cc',
    curriculumPDFLink: '',
    trialCourseQuery: 'coding',
    isBestSeller: false,
    coursePageUrl: COURSE_PAGE_URL.codingchampion,
    gradeOptions: [1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12],
    isAICourse: true,
    curriculumVideos: [
      {
        grades: '1-3',
        url:
          'https://iframe.mediadelivery.net/embed/239253/15077303-b4f6-4d34-b1b3-334738e09376?autoplay=true&loop=false&muted=false&preload=true&responsive=true',
        altText: 'AI & Coding Champion Course Video for Grade 1-3',
        image: 'cgm-1-3',
      },
      {
        grades: '4-5',
        url:
          'https://iframe.mediadelivery.net/embed/239253/4f10eb63-41fe-40b3-991f-b2d8be065efe?autoplay=true&loop=false&muted=false&preload=true&responsive=true',
        altText: 'AI & Coding Champion Course Video for Grade 4-5',
        image: 'cgm-4-5',
      },
      {
        grades: '6-8',
        url:
          'https://iframe.mediadelivery.net/embed/239253/52b47be1-02ba-42d1-be12-51dace30f31b?autoplay=true&loop=false&muted=false&preload=true&responsive=true',
        altText: 'AI & Coding Champion Course Video for Grade 6-8',
        image: 'cgm-6-8',
      },
      {
        grades: '9-10',
        url:
          'https://iframe.mediadelivery.net/embed/239253/3a0664ee-e6aa-434f-a6ce-2a4f27f4f36f?autoplay=true&loop=false&muted=false&preload=true&responsive=true',
        altText: 'AI & Coding Prodigy Course Video for Grade 9-10',
        image: 'cgm-9-10',
      },
      {
        grades: '11-12',
        url:
          'https://iframe.mediadelivery.net/embed/239253/649ee9e2-668a-4bc4-9a79-c7e92632d3bb?autoplay=true&loop=false&muted=false&preload=true&responsive=true',
        altText: 'AI & Coding Prodigy Course Video for Grade 11-12',
        image: 'cgm-11-12',
      },
    ],
  },
  sp: {
    title: <Trans>Scratch Programming with AI: Unleash Your Creativity</Trans>,
    titleCompactView: <Trans>Scratch Programming with AI</Trans>,
    grade: '1-8',
    activites: 50,
    lessons: 36,
    duration: '2-4',
    Age: '5-14',
    description: defineMessage({
      message:
        'Master AI and coding with Scratch: create games, animations, explore AI, and unlock endless creative potential.',
    }),
    image: 'scratch-programming',
    altText: 'Online Scratch Programming Course for Kids & Teens',
    outcomes: [
      defineMessage({ message: 'Learn block-based coding' }),
      defineMessage({ message: 'Create fun animations & games' }),
      defineMessage({
        message: 'Explore the basics of Artificial Intelligence',
      }),
      defineMessage({ message: 'Design interactive stories & digital art' }),
    ],
    courseCode: 'sp',
    curriculumPDFLink:
      'https://codingal.s3.ap-south-1.amazonaws.com/web/pdfs/curriculum/courses/v7/codingal-scratch-curriculum.pdf',
    trialCourseQuery: 'scratch-programming-trial-class',
    isBestSeller: false,
    coursePageUrl: COURSE_PAGE_URL.scratchPrograming,
    isAICourse: true,
  },
  gd: {
    title: <Trans>Game Development for Kids: Build Fun Games with AI</Trans>,
    titleCompactView: <Trans>Game Development for Kids</Trans>,
    grade: '4-8',
    activites: 50,
    lessons: 36,
    duration: '2-4',
    Age: '8-14',
    description: defineMessage({
      message:
        'Boost your AI and game development skills with Scratch and Code.org—create epic games and code like a pro!',
    }),
    image: 'game-development',
    altText: 'Online Game Development Course for Kids to Build Fun Games',
    outcomes: [
      defineMessage({
        message: 'Master AI & Coding Skills for Game Development',
      }),
      defineMessage({
        message: 'Apply Skills to Real-World Projects',
      }),
      defineMessage({ message: 'Think Like a Gamer, Code Like a Pro' }),
      defineMessage({
        message: 'Animate Characters and Objects to Bring Your Games to Life',
      }),
    ],
    courseCode: 'gd',
    curriculumPDFLink:
      'https://codingal.s3.ap-south-1.amazonaws.com/web/pdfs/curriculum/courses/v7/game-development.pdf',
    trialCourseQuery: 'game-development-trial-class',
    isBestSeller: false,
    coursePageUrl: COURSE_PAGE_URL.gameDevelopment,
    isAICourse: true,
  },
  ad: {
    title: <Trans>App Development for Kids: Create AI-Powered Apps</Trans>,
    titleCompactView: <Trans>App Development for Kids</Trans>,
    grade: '4-8',
    activites: 50,
    lessons: 36,
    duration: '2-4',
    Age: '8-14',
    description: defineMessage({
      message:
        'Dive into AI app development—where creativity meets tech to turn imagination into intelligent apps!',
    }),
    image: 'app-development',
    altText: 'Online App Development Course for Kids to Create AI-Powered Apps',
    outcomes: [
      defineMessage({ message: 'Build interactive interfaces' }),
      defineMessage({ message: 'Design awesome apps' }),
      defineMessage({
        message:
          'Make your app super smart (integrate camera, accelerometer, GPS, etc.)',
      }),
      defineMessage({ message: 'Share your app with the world' }),
    ],
    courseCode: 'ad',
    curriculumPDFLink:
      'https://codingal.s3.ap-south-1.amazonaws.com/web/pdfs/curriculum/courses/v7/app-development.pdf',
    trialCourseQuery: 'app-development-trial-class',
    isBestSeller: false,
    coursePageUrl: COURSE_PAGE_URL.appDevelopment,
    isAICourse: true,
  },
  rc: {
    title: <Trans>Roblox Champion: Build & Design Epic Games</Trans>,
    titleCompactView: <Trans>Roblox Champion</Trans>,
    grade: '4-8',
    activites: 50,
    lessons: 36,
    duration: '2-4',
    Age: '8-14',
    description: defineMessage({
      message:
        'Create and launch Roblox games with expert tutors. Turn your ideas into exciting games!',
    }),
    image: 'roblox-champion',
    altText: 'Online Roblox Course for Kids to Build & Design Epic Games',
    outcomes: [
      defineMessage({ message: 'Become a Roblox game builder' }),
      defineMessage({ message: 'Learn scripting with Lua' }),
      defineMessage({
        message:
          'Design fun & unique 3D games (tycoon simulations, racing games, horror experiences)',
      }),
      defineMessage({ message: 'Share your games with the world' }),
    ],
    courseCode: 'rc',
    curriculumPDFLink:
      'https://codingal.s3.ap-south-1.amazonaws.com/web/pdfs/curriculum/courses/v7/roblox-curriculum.pdf',
    trialCourseQuery: 'roblox-trial-class',
    isBestSeller: false,
    coursePageUrl: COURSE_PAGE_URL.roblox,
  },
  wd: {
    title: <Trans>Website Development: Build AI-Powered Websites</Trans>,
    titleCompactView: <Trans>Website Development</Trans>,
    grade: '8-12',
    activites: 90,
    lessons: 90,
    duration: '10-12',
    Age: '13-17',
    description: defineMessage({
      message:
        'Learn the fundamentals of the web and enhance your skills in building Interactive web pages using HTML, CSS, JavaScript, and more.',
    }),
    image: 'web-development',
    altText:
      'Website Development Course for Teens to Learn to Build and deploy Websites',
    outcomes: [
      defineMessage({ message: 'Build stunning, responsive websites' }),
      defineMessage({ message: 'Create interactive web pages' }),
      defineMessage({ message: 'Master HTML, CSS, and JavaScript' }),
      defineMessage({
        message: 'Implement best practices in website development',
      }),
    ],
    courseCode: 'wd',
    curriculumPDFLink:
      'https://codingal.s3.amazonaws.com/media/curriculum/pdfs/2026/07/25/Website_development_2026_QkoJgHL.pdf',
    trialCourseQuery: 'web-development-trial-class',
    isBestSeller: false,
    coursePageUrl: COURSE_PAGE_URL.webDevelopment,
  },
  pc: {
    title: <Trans>Python Champion: Enhance Your Skills</Trans>,
    titleCompactView: <Trans>Python Champion</Trans>,
    grade: '6-12',
    activites: 90,
    lessons: 44,
    duration: '4-6',
    Age: '11-17',
    description: defineMessage({
      message:
        'Dive into Python with hands-on projects. Explore the basics, build games, design apps, and unlock advanced libraries, OOP, and more!',
    }),
    image: 'python-champion',
    altText: 'Online Python Course for Teens to Enhance Coding Skills',
    outcomes: [
      defineMessage({ message: 'Master Python basics' }),
      defineMessage({ message: 'Build awesome interactive games' }),
      defineMessage({ message: 'Create cool GUI apps' }),
      defineMessage({ message: 'Learn object-oriented programming (OOP)' }),
    ],
    courseCode: 'pc',
    curriculumPDFLink:
      'https://codingal.s3.amazonaws.com/media/curriculum/pdfs/2026/07/25/Python_Champion_2026.pdf',
    curriculumVideos: [
      {
        url: COURSE_FIRST_FOLD.PYTHON_FOR_KIDS.videoURL,
      },
    ],
    trialCourseQuery: 'python-for-kids-trial-class',
    isBestSeller: false,
    coursePageUrl: COURSE_PAGE_URL.pythonForKids,
  },
  ds: {
    title: <Trans>AI & Data Science for Teens: Learn Python & Analytics</Trans>,
    titleCompactView: <Trans>AI & Data Science for Teens</Trans>,
    grade: '9-12',
    activites: 213,
    lessons: 144,
    duration: '18-20',
    Age: '13-17',
    description: defineMessage({
      message:
        'Master data science principles and gain practical experience through hands-on projects.',
    }),
    image: 'data-science',
    altText: 'Data Science Course for Teens with Python & Analytics Skills',
    outcomes: [
      defineMessage({ message: 'Master advanced Python libraries' }),
      defineMessage({ message: 'Learn data structures and algorithms' }),
      defineMessage({ message: 'Analyze and interpret complex datasets' }),
      defineMessage({
        message: 'Build machine learning and deep learning projects',
      }),
    ],
    courseCode: 'ds',
    curriculumPDFLink:
      'https://codingal.s3.amazonaws.com/media/curriculum/pdfs/2026/07/25/AI__Data_Science_2026.pdf',
    trialCourseQuery: 'data-science-trial-class',
    isBestSeller: false,
    coursePageUrl: COURSE_PAGE_URL.dataScience,
    isAICourse: true,
  },
  ioi: {
    title: <Trans>IOI Algorithms: Master Algorithms with Python</Trans>,
    titleCompactView: <Trans>IOI Algorithms</Trans>,
    grade: '9-12',
    activites: 150,
    lessons: 120,
    duration: '12-14',
    Age: '13-17',
    description: defineMessage({
      message:
        'Develop advanced skills in data structures and algorithms through hands-on problem-solving.',
    }),
    image: 'ioi-algorithms',
    altText: 'IOI Algorithms Course for Teens to Master Algorithms with Python',
    outcomes: [
      defineMessage({ message: 'Master data structures and algorithms' }),
      defineMessage({ message: 'Sharpen problem-solving skills' }),
      defineMessage({ message: 'Enhance Python programming skills' }),
      defineMessage({ message: 'Prepare for coding competitions' }),
    ],
    courseCode: 'ioi',
    curriculumPDFLink:
      'https://codingal.s3.ap-south-1.amazonaws.com/web/pdfs/curriculum/courses/v7/ioi-algorithms.pdf',
    trialCourseQuery: 'algorithms',
    isBestSeller: false,
    coursePageUrl: COURSE_PAGE_URL.IoiAlgorithms,
    prerequisite: <Trans>Basic Python & Data Structures (Grades 9+)</Trans>,
  },
  apcsa: {
    title: <Trans>AP Computer Science A: Programming with Java</Trans>,
    titleCompactView: <Trans>AP Computer Science A</Trans>,
    grade: '11-12',
    activites: 70,
    lessons: 48,
    duration: '4-6',
    Age: '15-17',
    description: defineMessage({
      message:
        'Master Java programming, OOP, data structures, and algorithms through hands-on problem-solving.',
    }),
    image: 'ap-computer-science-a',
    altText:
      'AP Computer Science A Course for Teens to Learn Programming with Java',
    outcomes: [
      defineMessage({ message: 'Master Java programming' }),
      defineMessage({ message: 'Understand & apply OOP concepts' }),
      defineMessage({ message: 'Enhance algorithmic thinking' }),
      defineMessage({ message: 'Develop advanced problem-solving skills' }),
    ],
    courseCode: 'apcsa',
    curriculumPDFLink:
      'https://codingal.s3.amazonaws.com/media/curriculum/pdfs/2026/07/25/AP_Computer_Science_A_2026.pdf',
    trialCourseQuery: 'ap-computer-science-a-trial-class',
    isBestSeller: false,
    coursePageUrl: COURSE_PAGE_URL.APComputerScienceA,
  },
  aigc: {
    title: <Trans>AI Genius: ChatGPT, AI-powered Apps & Games</Trans>,
    titleCompactView: <Trans>AI Genius</Trans>,
    grade: '3-7',
    activites: 48,
    lessons: 48,
    duration: '4-6',
    Age: '7-14',
    description: defineMessage({
      message:
        'In this 48-lesson course, kids dive into AI, creating art, chatbots, and games, learning image, sound, pose recognition, and a bit of ChatGPT magic!',
    }),
    image: 'ai-genius',
    altText:
      'AI Genius - ChatGPT, AI-powered Apps & Games for Kids to Learn AI & Programming',
    outcomes: [
      defineMessage({ message: 'Create AI Games and Apps' }),
      defineMessage({ message: 'Build Chatbots and AI Models' }),
      defineMessage({ message: 'Train AI to Recognize and Respond' }),
      defineMessage({ message: 'Master AI Concepts Through Projects' }),
    ],
    courseCode: 'aigc',
    curriculumVideos: [
      {
        url:
          'https://iframe.mediadelivery.net/embed/239253/23f4474f-f476-4a1a-94ee-07f81548a58e?autoplay=true&loop=false&muted=false&preload=true&responsive=true',
      },
    ],
    curriculumPDFLink:
      'https://codingal.s3.ap-south-1.amazonaws.com/web/pdfs/curriculum/courses/v7/ai-genius.pdf',
    trialCourseQuery: 'ai-trial-class',
    isBestSeller: false,
    coursePageUrl: COURSE_PAGE_URL.aiAndMl,
    isAICourse: true,
  },
  aiepc: {
    title: <Trans>AI Expert - Hands-On AI, LLMs & Python</Trans>,
    titleCompactView: <Trans>AI Expert</Trans>,
    grade: '8-12',
    activites: 48,
    lessons: 48,
    duration: '4-6',
    Age: '12-17',
    description: defineMessage({
      message:
        'In this 48-lesson advanced AI & ML course, students use LLMs and APIs to create projects like chatbots, translators, and image recognition tools.',
    }),
    image: 'ai-expert',
    altText:
      'AI Expert - Hands-On AI, LLMs & Python for Teens to Learn AI & Coding',
    outcomes: [
      defineMessage({ message: 'Build AI applications with APIs' }),
      defineMessage({ message: 'Develop AI-powered tools and assistants' }),
      defineMessage({
        message: 'Master AI techniques like NLP and computer vision',
      }),
      defineMessage({ message: 'Create and deploy real-time AI models' }),
    ],
    courseCode: 'aiepc',
    curriculumPDFLink:
      'https://codingal.s3.ap-south-1.amazonaws.com/web/pdfs/curriculum/courses/v7/ai-expert.pdf',
    curriculumVideos: [
      {
        url:
          'https://iframe.mediadelivery.net/embed/239253/56fb3546-d00a-45cd-bed7-38e648680fa6?autoplay=true&loop=false&muted=false&preload=true&responsive=true',
      },
    ],
    trialCourseQuery: 'ai-trial-class',
    isBestSeller: false,
    coursePageUrl: COURSE_PAGE_URL.aiAndMl,
    isAICourse: true,
  },
  bclc: {
    title: <Trans>Block Coding Legend: Advanced Level</Trans>,
    titleCompactView: <Trans>Block Coding Legend</Trans>,
    grade: '1-5',
    activites: 30,
    lessons: 48,
    duration: '6',
    Age: '6-12',
    description: defineMessage({
      message:
        'Master advanced block coding by building games, apps, animations, and AI creations across multiple platforms.',
    }),
    image: 'block-coding-adv',
    altText: 'Block Coding Legend: Advanced Level',
    outcomes: [
      defineMessage({ message: 'Create games using Code.org, Scratch' }),
      defineMessage({ message: 'Build apps with Thunkable, MIT App Inventor' }),
      defineMessage({
        message: 'Explore AI through AICode101 projects',
      }),
      defineMessage({ message: 'Earn a Block Coding Legend certificate' }),
    ],
    courseCode: 'bclc',
    curriculumPDFLink:
      'https://codingal.s3.ap-south-1.amazonaws.com/web/pdfs/curriculum/courses/v7/block-coding-advance-level.pdf',
    prerequisite: (
      <Trans>Foundational understanding of block-based coding</Trans>
    ),
    trialCourseQuery: 'scratch-programming-trial-class',
    isBestSeller: false,
    isAICourse: true,
  },
  psc101_3_5: {
    title: <Trans>Public Speaking &amp; Communication 101</Trans>,
    titleCompactView: <Trans>Public Speaking &amp; Communication 101</Trans>,
    grade: '3-5',
    activites: 8,
    lessons: 8,
    duration: '1-2',
    Age: '8-10',
    description: defineMessage({
      message:
        'Build foundational public speaking skills through structured practice in voice, body language, and short presentations.',
    }),
    image: 'public-speaking-and-communication-101',
    altText: 'Public speaking and communication 101',
    outcomes: [
      defineMessage({
        message:
          'Deliver short talks with a clear opening, main point, and closing',
      }),
      defineMessage({
        message:
          'Use voice modulation (pitch, pace, emphasis) to speak effectively',
      }),
      defineMessage({
        message:
          'Practice active listening and respond thoughtfully in conversations',
      }),
      defineMessage({
        message:
          'Tell structured stories with a logical flow and smooth transitions',
      }),
    ],
    courseCode: 'psc101_3_5',
    curriculumPDFLink: PSC_COURSES_GRADE_3_5.psc101_3_5.path,
    trialCourseQuery: 'public-speaking-trial-class',
    isBestSeller: false,
  },
  pscs_3_5: {
    title: <Trans>Public Speaking &amp; Communication Star</Trans>,
    titleCompactView: <Trans>Public Speaking &amp; Communication Star</Trans>,
    grade: '3-5',
    activites: 30,
    lessons: 24,
    duration: '2-3',
    Age: '8-10',
    description: defineMessage({
      message:
        'Develop confident stage presence, structured speeches, and persuasive communication through guided speaking practice.',
    }),
    image: 'public-speaking-and-communication-star',
    altText: 'Public speaking and communication star',
    outcomes: [
      defineMessage({
        message:
          'Deliver clear speeches with an introduction, key points, and closing',
      }),
      defineMessage({
        message:
          'Show confident stage presence with steady posture & purposeful gestures',
      }),
      defineMessage({
        message: 'Speak fluently with fewer filler words and better clarity',
      }),
      defineMessage({
        message: 'Share persuasive ideas with simple, well-organised reasoning',
      }),
    ],
    courseCode: 'pscs_3_5',
    curriculumPDFLink: PSC_COURSES_GRADE_3_5.pscs_3_5.path,
    trialCourseQuery: 'public-speaking-trial-class',
    isBestSeller: false,
  },
  pscc_3_5: {
    title: <Trans>Public Speaking &amp; Communication Champion</Trans>,
    titleCompactView: (
      <Trans>Public Speaking &amp; Communication Champion</Trans>
    ),
    grade: '3-5',
    activites: 50,
    lessons: 48,
    duration: '6-8',
    Age: '8-10',
    description: defineMessage({
      message:
        'Master persuasive speaking, audience engagement, and structured debate through focused communication skills practice.',
    }),
    image: 'public-speaking-and-communication-champion',
    altText: 'Public speaking and communication champion',
    outcomes: [
      defineMessage({
        message: 'Deliver persuasive speeches with clear points and examples',
      }),
      defineMessage({
        message: 'Use confident stage presence and engage the audience',
      }),
      defineMessage({
        message: 'Share viewpoints with logical, well-organised reasoning',
      }),
      defineMessage({
        message:
          'Join structured discussions and respond thoughtfully to others',
      }),
    ],
    courseCode: 'pscc_3_5',
    curriculumPDFLink: PSC_COURSES_GRADE_3_5.pscc_3_5.path,
    trialCourseQuery: 'public-speaking-trial-class',
    isBestSeller: false,
  },
  pscp_3_5: {
    title: <Trans>Public Speaking &amp; Communication Prodigy</Trans>,
    titleCompactView: (
      <Trans>Public Speaking &amp; Communication Prodigy</Trans>
    ),
    grade: '3-5',
    activites: 100,
    lessons: 96,
    duration: '9-12',
    Age: '8-10',
    description: defineMessage({
      message:
        'Build advanced argumentation, formal debate, and research-based presentation skills through in-depth speaking practice.',
    }),
    image: 'public-speaking-and-communication-prodigy',
    altText: 'Public speaking and communication Prodigy',
    outcomes: [
      defineMessage({
        message: 'Construct clear arguments supported by reasons and evidence',
      }),
      defineMessage({
        message:
          'Participate in formal debates with structured points and rebuttals',
      }),
      defineMessage({
        message:
          'Analyse different viewpoints and respond with logical counterpoints',
      }),
      defineMessage({
        message:
          'Deliver research-based presentations with clarity and credibility',
      }),
    ],
    courseCode: 'pscp_3_5',
    curriculumPDFLink: PSC_COURSES_GRADE_3_5.pscp_3_5.path,
    trialCourseQuery: 'public-speaking-trial-class',
    isBestSeller: false,
  },
  pscgm_3_5: {
    title: <Trans>Public Speaking &amp; Communication Grandmaster</Trans>,
    titleCompactView: (
      <Trans>Public Speaking &amp; Communication Grandmaster</Trans>
    ),
    grade: '3-5',
    activites: 150,
    lessons: 144,
    duration: '12-18',
    Age: '8-10',
    description: defineMessage({
      message:
        'Achieve mastery in persuasive speaking, formal debate, and authoritative public communication across academic settings.',
    }),
    image: 'public-speaking-and-communication-grandmaster',
    altText: 'Public speaking and communication Grandmaster',
    outcomes: [
      defineMessage({
        message: 'Deliver extended, persuasive speeches with clear structure',
      }),
      defineMessage({
        message: 'Lead formal debates using logic and strong rebuttals',
      }),
      defineMessage({
        message:
          'Evaluate ideas critically and present conclusions confidently',
      }),
      defineMessage({
        message: 'Show authoritative presence in academic and public settings',
      }),
    ],
    courseCode: 'pscgm_3_5',
    curriculumPDFLink: PSC_COURSES_GRADE_3_5.pscgm_3_5.path,
    trialCourseQuery: 'public-speaking-trial-class',
    isBestSeller: false,
  },
  psc101_12: {
    title: <Trans>Public Speaking &amp; Communication 101</Trans>,
    titleCompactView: <Trans>PSC 101 — Grade 1-2</Trans>,
    grade: '1-2',
    activites: 8,
    lessons: 8,
    duration: '1-2',
    Age: '6-8',
    description: defineMessage({
      message:
        'Young learners in Grade 1-2 build early speaking confidence through fun guided activities including picture descriptions, storytelling, and simple self-introductions.',
    }),
    image: 'public-speaking-and-communication-101',
    altText: 'Public speaking and communication 101 for Grade 1-2',
    outcomes: [
      defineMessage({
        message: 'Give a clear, confident self-introduction',
      }),
      defineMessage({
        message: 'Use good posture, eye contact, and simple gestures',
      }),
      defineMessage({
        message: 'Change voice (loud/soft, fast/slow) to show emotions',
      }),
      defineMessage({
        message: 'Tell a short story with a beginning, middle, and end',
      }),
    ],
    courseCode: 'psc101_12',
    curriculumPDFLink: PSC_COURSES_GRADE_1_2.psc101_12.path,
    trialCourseQuery: 'public-speaking-trial-class',
    isBestSeller: false,
  },
  pscs_12: {
    title: <Trans>Public Speaking &amp; Communication Star</Trans>,
    titleCompactView: <Trans>PSC Star — Grade 1-2</Trans>,
    grade: '1-2',
    activites: 30,
    lessons: 24,
    duration: '2-3',
    Age: '6-8',
    description: defineMessage({
      message:
        'Grade 1-2 learners build on early speaking skills with expressive reading, voice modulation, and simple storytelling activities designed for young communicators.',
    }),
    image: 'public-speaking-and-communication-star',
    altText: 'Public speaking and communication star for Grade 1-2',
    outcomes: [
      defineMessage({
        message: 'Speak confidently to a small audience',
      }),
      defineMessage({
        message: 'Project your voice and use expressive body language',
      }),
      defineMessage({
        message: 'Organise thoughts before speaking using simple planning',
      }),
      defineMessage({
        message: 'Deliver short, structured speeches clearly',
      }),
    ],
    courseCode: 'pscs_12',
    curriculumPDFLink: PSC_COURSES_GRADE_1_2.pscs_12.path,
    trialCourseQuery: 'public-speaking-trial-class',
    isBestSeller: false,
  },
  pscc_12: {
    title: <Trans>Public Speaking &amp; Communication Champion</Trans>,
    titleCompactView: <Trans>PSC Champion — Grade 1-2</Trans>,
    grade: '1-2',
    activites: 50,
    lessons: 48,
    duration: '6-8',
    Age: '6-8',
    description: defineMessage({
      message:
        'Grade 1-2 students develop structured speaking habits, learn to organise their ideas, and gain confidence presenting in front of peers and adults.',
    }),
    image: 'public-speaking-and-communication-champion',
    altText: 'Public speaking and communication champion for Grade 1-2',
    outcomes: [
      defineMessage({
        message: 'Speak clearly and confidently for longer',
      }),
      defineMessage({
        message: 'Combine voice, expressions, and gestures while presenting',
      }),
      defineMessage({
        message: 'Share opinions with simple reasons',
      }),
      defineMessage({
        message: 'Listen and respond politely in guided discussions',
      }),
    ],
    courseCode: 'pscc_12',
    curriculumPDFLink: PSC_COURSES_GRADE_1_2.pscc_12.path,
    trialCourseQuery: 'public-speaking-trial-class',
    isBestSeller: false,
  },
  pscp_12: {
    title: <Trans>Public Speaking &amp; Communication Prodigy</Trans>,
    titleCompactView: <Trans>PSC Prodigy — Grade 1-2</Trans>,
    grade: '1-2',
    activites: 100,
    lessons: 96,
    duration: '9-12',
    Age: '6-8',
    description: defineMessage({
      message:
        'Grade 1-2 students strengthen their communication skills through structured activities that build confidence in explaining ideas, supporting opinions, and participating in discussions.',
    }),
    image: 'public-speaking-and-communication-prodigy',
    altText: 'Public speaking and communication prodigy for Grade 1-2',
    outcomes: [
      defineMessage({
        message: 'Explain ideas clearly with examples',
      }),
      defineMessage({
        message: 'Support opinions with reasons and facts',
      }),
      defineMessage({
        message: 'Listen and respond thoughtfully in discussions',
      }),
      defineMessage({
        message: 'Speak confidently in structured debates',
      }),
    ],
    courseCode: 'pscp_12',
    curriculumPDFLink: PSC_COURSES_GRADE_1_2.pscp_12.path,
    trialCourseQuery: 'public-speaking-trial-class',
    isBestSeller: false,
  },
  pscgm_12: {
    title: <Trans>Public Speaking &amp; Communication Grandmaster</Trans>,
    titleCompactView: <Trans>PSC Grandmaster — Grade 1-2</Trans>,
    grade: '1-2',
    activites: 150,
    lessons: 144,
    duration: '12-18',
    Age: '6-8',
    description: defineMessage({
      message:
        'Grade 1-2 students achieve mastery in public speaking with advanced activities that develop independent speaking, strong voice control, and respectful group communication.',
    }),
    image: 'public-speaking-and-communication-grandmaster',
    altText: 'Public speaking and communication grandmaster for Grade 1-2',
    outcomes: [
      defineMessage({
        message: 'Speak independently with clear structure and confidence',
      }),
      defineMessage({
        message: 'Use strong voice control and purposeful body language',
      }),
      defineMessage({
        message: 'Support ideas with clear reasoning',
      }),
      defineMessage({
        message: 'Engage respectfully in longer group discussions',
      }),
    ],
    courseCode: 'pscgm_12',
    curriculumPDFLink: PSC_COURSES_GRADE_1_2.pscgm_12.path,
    trialCourseQuery: 'public-speaking-trial-class',
    isBestSeller: false,
  },
}
// TODO: Add Course Video
export const COURSE_VIDEO: Record<string, CourseVideoType[]> = {
  AI_AND_ML: [
    {
      title: 'AI Genius',
      url:
        'https://iframe.mediadelivery.net/embed/239253/23f4474f-f476-4a1a-94ee-07f81548a58e?autoplay=true&loop=false&muted=false&preload=true&responsive=true',
      image: 'ai-genius-video-thumbnail',
      altText: 'AI Genius course video thumbnail for kids',
    },
    {
      title: 'AI Expert',
      url:
        'https://iframe.mediadelivery.net/embed/239253/56fb3546-d00a-45cd-bed7-38e648680fa6?autoplay=true&loop=false&muted=false&preload=true&responsive=true',
      image: 'ai-expert-video-thumbnail',
      altText: 'AI Expert course video thumbnail for kids & teens',
    },
  ],
  // THUNKABLE: [
  //   {
  //     title: 'What is Thunkable?',
  //     image: 'what-is-thunkable-video-thumbnail',
  //     altText: 'What is Thunkable course video thumbnail for kids',
  //   },
  //   {
  //     title: 'Thunkable Curriculum!',
  //     image: 'thunkable-curriculum-video-thumbnail',
  //     altText: 'Thunkable Curriculum course video thumbnail for kids',
  //   },
  // ],
  // JAVA: [
  //   {
  //     title: 'What is Java?',
  //     image: 'what-is-java-video-thumbnail',
  //     altText: 'What is Java course video thumbnail for kids',
  //   },
  //   {
  //     title: 'Java Curriculum!',
  //     image: 'java-curriculum-video-thumbnail',
  //     altText: 'Java Curriculum course video thumbnail for kids',
  //   },
  // ],
  // TRINKET: [
  //   {
  //     title: 'What is Trinket?',
  //     image: 'what-is-trinket-video-thumbnail',
  //     altText: 'What is Trinket course video thumbnail for kids',
  //   },
  //   {
  //     title: 'Trinket Curriculum!',
  //     image: 'trinket-curriculum-video-thumbnail',
  //     altText: 'Trinket Curriculum course video thumbnail for kids',
  //   },
  // ],
  // PYGAME: [
  //   {
  //     title: 'What is Pygame?',
  //     image: 'what-is-pygame-video-thumbnail',
  //     altText: 'What is Pygame course video thumbnail for kids',
  //   },
  //   {
  //     title: 'Pygame Curriculum!',
  //     image: 'pygame-curriculum-video-thumbnail',
  //     altText: 'Pygame Curriculum course video thumbnail for kids',
  //   },
  // ],
  // ANIMATION: [
  //   {
  //     title: 'What is Animation?',
  //     image: 'what-is-animation-video-thumbnail',
  //     altText: 'What is Animation course video thumbnail for kids',
  //   },
  //   {
  //     title: 'Animation Curriculum!',
  //     image: 'animation-curriculum-video-thumbnail',
  //     altText: 'Animation Curriculum course video thumbnail for kids',
  //   },
  // ],
}

export const COURSE_INCLUDES = {
  MIT_APP_INVENTOR: [
    {
      icon: 'integrated-development',
      title: 'Mobile App Development for Beginners',
      description:
        'Our MIT App Inventor lessons are perfect for beginners, introducing mobile app development through a simple drag-and-drop coding interface. Students will learn how to build interactive apps like a quiz app, health tracker, and doodling board, while mastering the basics of UI design, logic, and user interaction.',
    },
    {
      icon: 'mobile-app',
      title: 'Build Your First Apps',
      description:
        'In our MIT App Inventor courses, students dive into mobile app development by creating projects such as a health app, quiz app, and drawing pad. They explore components like buttons, web viewers, sensors, and layouts while gaining a solid understanding of how technology works and how to design user-friendly apps.',
    },
    {
      icon: 'introduction',
      title: 'Introduction to MIT App Inventor',
      description:
        'Our App Development Basics lessons introduce students to MIT App Inventor, guiding them through creating simple apps that they can use on their phones. Students learn to work with key components such as buttons, labels, web viewers, and sensors, while gaining essential skills in app design, logic, and interactivity.',
    },
    {
      icon: 'analytical-thinking',
      title: 'Creating Interactive Apps with MIT App Inventor',
      description:
        'In this course, students learn how to design fun and interactive apps such as a quiz app or health tracker. Using MIT App Inventor, they will explore how to integrate user input, buttons, labels, and sensors to create dynamic and functional apps. The focus is on mastering app development fundamentals while fostering creativity and problem-solving.',
    },
    {
      icon: 'health-app',
      title: 'Health App Development with MIT App Inventor',
      description:
        'Students will create a Health App using MIT App Inventor, where they will learn to track simple health metrics like steps or water intake. This project introduces students to the concept of sensors and user input, while helping them understand how to build apps that can collect and process real-world data.',
    },
  ],
}

export type CoursesProps = {
  subject?: 'Coding' | 'Math' | 'Public Speaking & Communication'
  homepageCourses?: HomepageCourse[]
  className?: string
  paddingClassName?: string
  ctaSize?: SizeType
  title?: string
  description?: string
  utmSource?: string
  slug?: string
}

export type HomepageCourse = {
  id: number
  title: string
  description: string | null
  thumbnailLandscape: string | null
  ageRange: string | null
  gradeRange: string | null
  estimatedCompletionTime: string | null
  totalLessonsCount: number
  totalActivitiesCount: number
  landingPageUrl: string | null
  curriculumPdf: string | null
  introVideoUrl: string | null
  trialCourseSlug: string | null
  learningOutcomes: string[]
}

const Courses = ({
  subject = 'Coding',
  homepageCourses = [],
  className,
  paddingClassName = 'py-12 lg:py-16',
  ctaSize = 'lg',
  title,
  description,
  utmSource,
  slug,
}: CoursesProps) => {
  const [isModalOpen, setIsModalOpen] = useState<boolean>(false)
  const [isVideoModalOpen, setIsVideoModalOpen] = useState<boolean>(false)
  const [isIframeVideoOpen, setIsIframeVideoOpen] = useState<boolean>(false)
  const [coursePDFCode, setCoursePDFCode] = useState<string | null>('cp')
  const [curriculumVideo, setCurriculumVideo] = useState<any[]>([])
  const [courseTitle, setCourseTitle] = useState<string>()
  const [gradeOptions, setGradeOptions] = useState<number[]>([
    1,
    2,
    3,
    4,
    5,
    6,
    7,
    8,
    9,
    10,
    11,
    12,
  ])

  const handlePDFDownload = (courseCode: string, gradeOptions: any) => {
    setCoursePDFCode(courseCode)
    setIsModalOpen(true)
    setGradeOptions(gradeOptions)
  }

  const handleVideoModalOpen = (
    videos: CurriculumVideoType[],
    title: string
  ) => {
    setCurriculumVideo(videos)
    if (videos.length > 1) {
      setIsVideoModalOpen(true)
      setCourseTitle(title)
    } else {
      setIsIframeVideoOpen(true)
    }
  }

  const getRandomHoverBg = () => {
    const randomIndex = Math.floor(Math.random() * HOVER_BG.length)
    return HOVER_BG[randomIndex]
  }

  // First 3 cards stay hardcoded in the frontend (as in master). The rest of
  // the homepage fold is driven by the official courses API.
  const pinnedCourses = HOMEPAGE_PINNED_COURSE_CODES.map(
    (code) => COURSE_INFO[code]
  )

  const filteredCourses =
    homepageCourses && homepageCourses.length > 0
      ? [
          ...pinnedCourses,
          ...homepageCourses.map((course) => ({
            id: course.id,
            title: course.title,
            titleCompactView: course.title,
            grade: course.gradeRange?.replace(/^Grade\s*/, '') || '',
            activites: course.totalActivitiesCount,
            lessons: course.totalLessonsCount,
            duration: course.estimatedCompletionTime || '',
            Age: course.ageRange?.replace(/^Age\s*/, '') || '',
            description: course.description || '',
            image: 'coding-prodigy',
            thumbnailLandscape: course.thumbnailLandscape,
            altText: `Image for ${course.title} course`,
            outcomes: course.learningOutcomes || [],
            courseCode: '',
            curriculumPDFLink: course.curriculumPdf || '',
            trialCourseQuery: course.trialCourseSlug || '',
            isBestSeller: false,
            coursePageUrl: course.landingPageUrl || '/courses/',
            isAICourse: course.title.toLowerCase().includes('ai'),
            hideDurationSuffix: true,
            curriculumVideos: course.introVideoUrl
              ? [{ url: toBunnyEmbedUrl(course.introVideoUrl) }]
              : undefined,
          })),
        ]
      : Object.values(COURSE_INFO).filter(
          ({ trialCourseQuery: q }) => getTrialSubject(q) === subject
        )

  getRandomHoverBg()

  return (
    <div className={className || 'bg-grey-100'}>
      <div
        className={`container mx-auto flex flex-col space-y-10 leading-tight text-grey px-5 ${paddingClassName}`}
        id="courses"
      >
        <CurriculumDownloadModal
          isModalOpen={isModalOpen}
          setIsModalOpen={setIsModalOpen}
          grades={gradeOptions}
          curriculumPDFPrefix={coursePDFCode.toLowerCase()}
        />
        <CourseVideoModal
          isModalOpen={isVideoModalOpen}
          setIsModalOpen={setIsVideoModalOpen}
          curriculumVideo={curriculumVideo}
          courseTitle={courseTitle}
          videoModalOpen={isIframeVideoOpen}
          setVideoModalOpen={setIsIframeVideoOpen}
        />
        <div className="text-center">
          <h2 className="text-2xl lg:text-4xl font-600 mb-4">
            {title ? (
              <>{title}</>
            ) : (
              <>
                <Trans>
                  Choose an{' '}
                  <span className="underlined-text">AI & coding course</span>{' '}
                  that excites your child
                </Trans>
              </>
            )}
          </h2>
          {description && (
            <p className="text-lg text-grey-800 text-center max-w-4xl mx-auto mb-12">
              {description}
            </p>
          )}
        </div>

        <div className="flex flex-wrap justify-center">
          {filteredCourses.map((course, i) => (
            <CourseCard
              key={i}
              course={course}
              onPDFDownload={handlePDFDownload}
              onVideoModalOpen={handleVideoModalOpen}
              utmSource={utmSource}
              slug={slug}
            />
          ))}
        </div>

        <div className="flex justify-center mt-8">
          <Link href="/courses/">
            <a>
              <Button variant="solid" intent="primary" size={ctaSize}>
                <Trans>Browse all courses</Trans>
              </Button>
            </a>
          </Link>
        </div>
      </div>
    </div>
  )
}

export default Courses
